/* ==========================================================================
   DTF Africa — behaviour for the custom `dtf-*` homepage sections.
   Self-initialising and idempotent: safe to run again when the theme editor
   re-renders a section (shopify:section:load).
   ========================================================================== */
(() => {
  'use strict';

  const FLAME = '#FD6300', WHITE = '#EEF0F8', MID = '#5566A8';
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- artwork marks (stand-ins for customer logos) ---------- */
  function drawMark(g, x, y, w, h, kind, fill) {
    g.save(); g.translate(x, y); g.fillStyle = fill; g.strokeStyle = fill;
    const r = Math.min(w, h);
    switch (kind) {
      case 0:
        g.fillRect(0, 0, w, h * .42);
        g.globalAlpha = .55;
        g.fillRect(0, h * .56, w * .82, h * .16);
        g.fillRect(0, h * .8, w * .5, h * .16);
        break;
      case 1:
        g.beginPath(); g.arc(w / 2, h / 2, r / 2, 0, 7); g.fill();
        g.globalCompositeOperation = 'destination-out';
        g.beginPath(); g.arc(w / 2, h / 2, r * .33, 0, 7); g.fill();
        g.globalCompositeOperation = 'source-over';
        g.beginPath(); g.arc(w / 2, h / 2, r * .18, 0, 7); g.fill();
        break;
      case 2:
        g.beginPath();
        g.moveTo(w / 2, 0); g.lineTo(w, h * .3); g.lineTo(w, h * .72);
        g.lineTo(w / 2, h); g.lineTo(0, h * .72); g.lineTo(0, h * .3);
        g.closePath(); g.fill();
        break;
      case 3:
        for (let i = 0; i < 3; i++) {
          g.globalAlpha = 1 - i * .26;
          g.fillRect(0, i * h * .38, w * (1 - i * .22), h * .24);
        }
        break;
      default:
        g.beginPath();
        for (let i = 0; i < 12; i++) {
          const a = i / 12 * Math.PI * 2, rr = i % 2 ? r * .22 : r * .5;
          g[i ? 'lineTo' : 'moveTo'](w / 2 + Math.cos(a) * rr, h / 2 + Math.sin(a) * rr);
        }
        g.closePath(); g.fill();
    }
    g.restore();
  }

  /* ---------- MaxRects packer (best-short-side-fit) ----------
     Keeps a set of maximal free rectangles, drops each piece into the
     tightest one, then re-splits every free rect it overlapped. A piece
     that doesn't fit is skipped, never a reason to stop packing. */
  function pack(W, H, pieces, gap) {
    const free = [{ x: 0, y: 0, w: W, h: H }], out = [];
    for (const p of pieces) {
      const pw = p.w + gap, ph = p.h + gap;
      let best = -1, bShort = Infinity, bLong = Infinity;
      for (let i = 0; i < free.length; i++) {
        const r = free[i];
        if (r.w < pw || r.h < ph) continue;
        const dw = r.w - pw, dh = r.h - ph;
        const s = Math.min(dw, dh), l = Math.max(dw, dh);
        if (s < bShort || (s === bShort && l < bLong)) { best = i; bShort = s; bLong = l; }
      }
      if (best < 0) continue;
      const n = { x: free[best].x, y: free[best].y, w: pw, h: ph };
      out.push({ ...p, x: n.x, y: n.y });

      for (let i = free.length - 1; i >= 0; i--) {
        const f = free[i];
        if (f.x >= n.x + n.w || f.x + f.w <= n.x ||
            f.y >= n.y + n.h || f.y + f.h <= n.y) continue;
        free.splice(i, 1);
        if (n.x > f.x)             free.push({ x: f.x, y: f.y, w: n.x - f.x, h: f.h });
        if (n.x + n.w < f.x + f.w) free.push({ x: n.x + n.w, y: f.y, w: f.x + f.w - n.x - n.w, h: f.h });
        if (n.y > f.y)             free.push({ x: f.x, y: f.y, w: f.w, h: n.y - f.y });
        if (n.y + n.h < f.y + f.h) free.push({ x: f.x, y: n.y + n.h, w: f.w, h: f.y + f.h - n.y - n.h });
      }
      for (let i = free.length - 1; i >= 0; i--) {
        const a = free[i];
        for (let j = 0; j < free.length; j++) {
          if (i === j) continue;
          const b = free[j];
          if (a.x >= b.x && a.y >= b.y &&
              a.x + a.w <= b.x + b.w && a.y + a.h <= b.y + b.h) { free.splice(i, 1); break; }
        }
      }
    }
    return out;
  }

  function makePieces(n, seed, scale) {
    let s = seed;
    const rnd = () => (s = (s * 1664525 + 1013904223) % 4294967296) / 4294967296;
    const a = [];
    for (let i = 0; i < n; i++) {
      const w = (44 + rnd() * 168) * scale;
      const h = w * (.38 + rnd() * .86);
      a.push({ w, h, kind: (rnd() * 5) | 0,
               fill: [FLAME, WHITE, MID, FLAME, WHITE][(rnd() * 5) | 0] });
    }
    return a.sort((p, q) => q.w * q.h - p.w * p.h);
  }

  function sheetBg(g, W, H) {
    g.fillStyle = '#040D2E'; g.fillRect(0, 0, W, H);
    g.strokeStyle = 'rgba(255,255,255,.05)'; g.lineWidth = 1;
    for (let i = 40; i < W; i += 40) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, H); g.stroke(); }
    for (let i = 40; i < H; i += 40) { g.beginPath(); g.moveTo(0, i); g.lineTo(W, i); g.stroke(); }
    g.strokeStyle = 'rgba(253,99,0,.5)'; g.lineWidth = 2;
    const c = 22;
    [[0,0,1,1],[W,0,-1,1],[0,H,1,-1],[W,H,-1,-1]].forEach(([cx, cy, sx, sy]) => {
      g.beginPath(); g.moveTo(cx + sx * c, cy); g.lineTo(cx, cy); g.lineTo(cx, cy + sy * c); g.stroke();
    });
  }

  /* Same grid, but on paper — the v4 homepage sits on white, so the dark
     sheetBg would punch a hole in it. */
  function sheetLight(g, W, H) {
    g.fillStyle = '#F5F6FB'; g.fillRect(0, 0, W, H);
    g.strokeStyle = 'rgba(2,11,65,.06)'; g.lineWidth = 1;
    for (let i = 40; i < W; i += 40) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, H); g.stroke(); }
    for (let i = 40; i < H; i += 40) { g.beginPath(); g.moveTo(0, i); g.lineTo(W, i); g.stroke(); }
  }

  function drawTee(g, ox, oy, w, h, body, stroke) {
    g.save(); g.translate(ox, oy);
    g.fillStyle = body; g.strokeStyle = stroke; g.lineWidth = 2;
    const sh = w * .30, sy = h * .17;
    g.beginPath();
    g.moveTo(w * .28, 0); g.lineTo(w * .72, 0);
    g.lineTo(w * .72 + sh, sy * .55); g.lineTo(w * .72 + sh * .82, sy * 1.25);
    g.lineTo(w * .86, sy * .98); g.lineTo(w * .86, h); g.lineTo(w * .14, h);
    g.lineTo(w * .14, sy * .98); g.lineTo(w * .28 - sh * .82, sy * 1.25);
    g.lineTo(w * .28 - sh, sy * .55); g.closePath(); g.fill(); g.stroke();
    g.beginPath(); g.moveTo(w * .28, 0);
    g.quadraticCurveTo(w * .5, h * .085, w * .72, 0);
    g.strokeStyle = 'rgba(255,255,255,.28)'; g.lineWidth = 3; g.stroke();
    g.restore();
  }

  /* ---------- hero backdrop: a nested sheet, quiet enough to sit behind type --- */
  function initHeroBg(root) {
    const cv = root.querySelector('[data-dtf-herobg]');
    if (!cv || cv.dataset.dtfReady) return;
    cv.dataset.dtfReady = '1';
    const g = cv.getContext('2d'), W = cv.width, H = cv.height;
    const grd = g.createLinearGradient(0, 0, W, H);
    grd.addColorStop(0, '#FFFFFF'); grd.addColorStop(.5, '#F5F6FB'); grd.addColorStop(1, '#ECEFF8');
    g.fillStyle = grd; g.fillRect(0, 0, W, H);
    const glow = g.createRadialGradient(W * .62, H * .44, 20, W * .62, H * .44, W * .55);
    glow.addColorStop(0, 'rgba(253,99,0,.16)'); glow.addColorStop(1, 'rgba(253,99,0,0)');
    g.fillStyle = glow; g.fillRect(0, 0, W, H);
    const hp = ['#C9CEE6', '#F4B183', '#DDE1EF', '#FD6300', '#AEB6DA'];
    g.save(); g.translate(W * .20, H * .10); g.rotate(-.045);
    g.strokeStyle = 'rgba(2,11,65,.10)'; g.lineWidth = 3;
    g.strokeRect(-10, -10, W * .66 + 20, H * .78 + 20);
    pack(W * .66, H * .78, makePieces(20, 21, 1.55), 18).forEach((pc, i) => {
      g.globalAlpha = (i % 5 === 3) ? .75 : .5;
      drawMark(g, pc.x, pc.y, pc.w, pc.h, pc.kind, hp[i % hp.length]);
    });
    g.restore(); g.globalAlpha = 1;
  }

  /* ---------- placeholder art for a split section with no image yet ---------- */
  function initSplitArt(root) {
    root.querySelectorAll('[data-dtf-splitart]').forEach(cv => {
      if (cv.dataset.dtfReady) return;
      cv.dataset.dtfReady = '1';
      const g = cv.getContext('2d'), W = cv.width, H = cv.height;
      const grd = g.createLinearGradient(0, 0, W, H);
      grd.addColorStop(0, '#0A1552'); grd.addColorStop(1, '#050D33');
      g.fillStyle = grd; g.fillRect(0, 0, W, H);
      const glow = g.createRadialGradient(W * .5, H * .35, 20, W * .5, H * .35, W * .85);
      glow.addColorStop(0, 'rgba(253,99,0,.22)'); glow.addColorStop(1, 'rgba(253,99,0,0)');
      g.fillStyle = glow; g.fillRect(0, 0, W, H);
      g.strokeStyle = 'rgba(255,255,255,.05)'; g.lineWidth = 1;
      for (let i = 50; i < W; i += 50) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, H); g.stroke(); }
      for (let i = 50; i < H; i += 50) { g.beginPath(); g.moveTo(0, i); g.lineTo(W, i); g.stroke(); }
      const cols = [FLAME, WHITE, MID, FLAME, '#8892C4'];
      let seed = 7;
      const rnd = () => (seed = (seed * 1664525 + 1013904223) % 4294967296) / 4294967296;
      for (let i = 0; i < 26; i++) {
        const w = 60 + rnd() * 210, h = 30 + rnd() * 120;
        g.globalAlpha = .18 + rnd() * .45;
        g.fillStyle = cols[(rnd() * cols.length) | 0];
        g.fillRect(rnd() * (W - w), rnd() * (H - h), w, h);
      }
      g.globalAlpha = 1;
    });
  }

  /* ---------- fitcheck: a customer's own artwork shown to scale on a garment ----------
     The upload never leaves the browser. The file is read to an object URL,
     drawn to the canvas, and revoked when it is replaced or cleared — nothing is
     posted anywhere and nothing is stored. The section says that on screen, so
     it has to stay true here. */
  function initFit(root) {
    const cv = root.querySelector('[data-dtf-fit]');
    if (!cv || cv.dataset.dtfReady) return;
    cv.dataset.dtfReady = '1';

    const scope = cv.closest('[data-dtf-fit-root]') || cv.closest('.dtf') || document;
    const wrap = scope.querySelector('[data-dtf-fit-sizes]');
    if (!wrap) return;

    const parse = (s, fb) => { try { return JSON.parse(s || ''); } catch (e) { return fb; } };
    /* Display units. Everything inside is millimetres — the size blocks, the
       garment panels, the order properties production reads — and only what a
       person sees is converted. cm was the client's ask (11 Sep 2026). */
    const CM = (scope.dataset.units || 'mm') === 'cm';
    const uf = CM ? 10 : 1;
    const fmtLen = mm => {
      if (!CM) return Math.round(mm * 10) / 10 + ' mm';
      const v = Math.round(mm) / 10;
      return (Number.isInteger(v) ? String(v) : v.toFixed(1)) + ' cm';
    };
    const fmtPair = (w, h) => CM
      ? (Number.isInteger(Math.round(w) / 10) ? String(Math.round(w) / 10) : (Math.round(w) / 10).toFixed(1)) + ' × ' + fmtLen(h)
      : Math.round(w * 10) / 10 + ' × ' + Math.round(h * 10) / 10 + ' mm';
    const sizes = parse(wrap.dataset.dtfFitSizes, []);
    if (!sizes.length) return;

    const g = cv.getContext('2d'), W = cv.width, H = cv.height;
    const q = sel => scope.querySelector(sel);
    const oSize = q('[data-dtf-fit-size]'),  oCov  = q('[data-dtf-fit-cover]');
    const oDpi  = q('[data-dtf-fit-dpi]'),   oPr   = q('[data-dtf-fit-price]');
    const oNote = q('[data-dtf-fit-note]');
    const inFile = q('[data-dtf-fit-file]'), drop  = q('[data-dtf-fit-drop]');
    const fileRow = q('[data-dtf-fit-filerow]'), fileName = q('[data-dtf-fit-filename]');
    const btnClear = q('[data-dtf-fit-clear]');
    const actions = q('[data-dtf-fit-actions]'), btnDl = q('[data-dtf-fit-download]');
    const gRow = q('[data-dtf-fit-garment-row]'), gUi = q('[data-dtf-fit-garments-ui]');
    const pRow = q('[data-dtf-fit-placement-row]'), pUi = q('[data-dtf-fit-placements]');
    const dragHint = q('[data-dtf-fit-draghint]');
    const thumb = q('[data-dtf-fit-thumb]');
    // The stage caption names the placement being shown. It keeps its authored
    // wording when the section has no placement blocks, so a garment with one
    // print position does not lose its caption to an empty string.
    const capOut = q('[data-dtf-fit-placement-name]');
    const alphaOut = q('[data-dtf-fit-alpha]');
    const btnRmBg = q('[data-dtf-fit-removebg]');
    const MSG_ALPHA = {
      ok: scope.dataset.msgAlphaOk || 'Transparent background detected',
      warn: scope.dataset.msgAlphaWarn || 'No transparent background. Anything white in this file prints as white ink, including the box around your design.',
      removed: scope.dataset.msgAlphaRemoved || 'Background removed automatically. Check the preview: any white inside your design has been removed too.'
    };

    /* Whether the artwork is cut out. DTF prints what it is given, so a logo on
       a solid white rectangle arrives as a white rectangle pressed onto the
       garment — the single most common reason a first order is rejected on
       sight. The border ring is what settles it: a cut-out design has clear
       pixels around its edge, a flattened one does not. A JPEG cannot carry
       alpha at all, so it always warns, which is correct. */
    function checkAlpha(im) {
      if (!alphaOut) return;
      let clear = null;
      try {
        const N = 140;
        const k = Math.min(1, N / Math.max(im.naturalWidth, im.naturalHeight));
        const w = Math.max(6, Math.round(im.naturalWidth * k));
        const h = Math.max(6, Math.round(im.naturalHeight * k));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const cg = c.getContext('2d', { willReadFrequently: true });
        cg.drawImage(im, 0, 0, w, h);
        const d = cg.getImageData(0, 0, w, h).data;
        let ring = 0, open = 0;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if (x > 1 && x < w - 2 && y > 1 && y < h - 2) continue;
            ring++;
            if (d[(y * w + x) * 4 + 3] < 16) open++;
          }
        }
        clear = ring > 0 ? (open / ring) > 0.6 : null;
      } catch (e) { clear = null; }

      // Offered only on a flattened file. On a cut-out one the corners are
      // transparent black, and the knockout below would erase the black in
      // the design itself.
      if (btnRmBg) btnRmBg.hidden = clear !== false || artIsVector;
      if (clear === null) {
        alphaOut.hidden = true; alphaOut.textContent = '';
      } else {
        // After our own knockout the edges are always clear, so a tick would
        // vouch for the one thing the check cannot see: white lost from
        // inside the design. Say that instead, in the neutral register.
        const removed = clear && artBgRemoved;
        alphaOut.hidden = false;
        alphaOut.textContent = removed ? MSG_ALPHA.removed : (clear ? MSG_ALPHA.ok : MSG_ALPHA.warn);
        alphaOut.classList.toggle('is-warn', !clear);
        alphaOut.classList.toggle('is-note', removed);
      }
      // The stepped buy box waits on this before advancing, so a flattened
      // file keeps the customer on the upload step with its warning in view.
      alphaOut.dispatchEvent(new CustomEvent('dtf:alpha', { bubbles: true, detail: { clear } }));
    }
    /* The ruler is the measuring overlay — the tinted rectangle, its dashed
       edge and the millimetre label. It is the whole point while you are
       deciding a size and it is a defect the moment the picture leaves the
       page, so it is always off in an export regardless of the toggle. A
       customer's saved mockup, and the one production sees on the order,
       should show the garment they are buying and not our measuring tool. */
    const btnRuler = q('[data-dtf-fit-ruler]');
    let showRuler = true, exporting = false;
    /* A section can hand the ruler to the artwork instead of a button: shown
       while choosing a size, gone the moment a file is placed (client call,
       16 Sep 2026). Sections that don't ask keep the toggle exactly as it was. */
    const rulerAuto = scope.dataset.rulerAuto === 'true';
    /* Where the artwork starts, in millimetres below the garment's print
       origin, before any placement or drag. The origin sits at the collar,
       which reads as too high for a chest print; 0 leaves other sections
       drawing exactly where they did. */
    const DROP = parseFloat(scope.dataset.artDropMm) || 0;

    // front panel of an adult size L tee, in millimetres — the fallback when no
    // garment block supplies its own measurements
    const TEE_W = 530, TEE_H = 740;
    const TARGET = parseFloat(scope.dataset.targetDpi) || 300;
    const FLOOR  = parseFloat(scope.dataset.floorDpi)  || 150;
    const MSG = {
      good: scope.dataset.msgGood || 'Good to print',
      ok:   scope.dataset.msgOk   || 'Usable, not ideal',
      bad:  scope.dataset.msgBad  || 'Too low to print well',
      big:  scope.dataset.msgOversize || 'This size is larger than the printable area on this garment.'
    };
    const editing = !!(window.Shopify && window.Shopify.designMode);
    // Dragging is pointer-device only. Claiming the canvas for touch would mean
    // touch-action:none on a very tall element, which costs the customer the
    // ability to scroll the page over the garment — the placement chips are the
    // touch answer instead.
    const canDrag = !!window.PointerEvent &&
      !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
    /* Touch dragging, for a section that has no other way to move the print
       (placement chips off, 16 Sep 2026). It is claimed per gesture rather than
       with touch-action on the canvas: a touch that starts ON the print drags
       it, one that starts anywhere else still scrolls the page. */
    const touchDrag = scope.dataset.touchDrag === 'true' && 'ontouchstart' in window;

    /* ---- garments ---------------------------------------------------------
       A garment block calibrates by measurement rather than by eye: the merchant
       says how wide the printable panel is in the photo and how wide it is in
       real life, and px-per-mm falls out of the two. The legacy single-photo
       settings are kept so the placements that already exist keep working. */
    let garments = parse(scope.dataset.dtfFitGarments, []).map(gm => ({
      label: gm.label || 'Garment',
      src: gm.src,
      ppm: (W * ((gm.panelPct || 50) / 100)) / (gm.panelW || TEE_W),
      cx: (gm.cx == null ? 50 : gm.cx) / 100,
      top: (gm.top == null ? 34 : gm.top) / 100,
      panelW: gm.panelW || TEE_W,
      panelH: gm.panelH || TEE_H,
      panelPct: (gm.panelPct || 50) / 100,
      panelTop: gm.panelTop == null ? null : gm.panelTop / 100,
      panelHPct: gm.panelHPct == null ? null : gm.panelHPct / 100,
      note: gm.note || null,
      bg: gm.bg || null,
      opt: gm.opt || null,
      hex: gm.hex || null,
      img: null
    }));

    if (!garments.length && cv.dataset.dtfFitImage) {
      garments = [{
        label: '', src: cv.dataset.dtfFitImage,
        ppm: (parseFloat(cv.dataset.dtfFitScale) || 74) / 100,
        cx: (parseFloat(cv.dataset.dtfFitCx) || 66) / 100,
        top: (parseFloat(cv.dataset.dtfFitTop) || 36) / 100,
        panelW: TEE_W, panelH: TEE_H, panelPct: null, panelTop: null, panelHPct: null,
        note: null, img: null
      }];
    }

    const placements = (pUi ? parse(pUi.dataset.dtfFitPlacements, []) : []).map(pl => ({
      label: pl.label || 'Placement',
      dx: parseFloat(pl.dx) || 0,
      dy: parseFloat(pl.dy) || 0,
      note: pl.note || null
    }));

    let start = sizes.findIndex(s => s.price);
    if (start < 0) start = 0;
    let cur = Object.assign({}, sizes[start]);
    let garment = garments[0] || null;
    let place = placements[0] || null;
    // free-hand nudge on top of the chosen placement, in millimetres, so it
    // survives a change of garment or size and resets when a placement is picked
    let nudgeX = 0, nudgeY = 0;

    /* ---- auto-fit ----------------------------------------------------------
       Calibration by hand is four eyeballed percentages, and getting the width
       one wrong scales every print on the page. The photo already knows the
       answer, so this reads it: find the garment against its background, then
       measure the body across the middle of it — halfway down a short-sleeve
       tee the sleeves have ended, so the opaque run at that height IS the body,
       side seam to side seam, which is the span the merchant is being asked to
       trace. Top and bottom of the silhouette give the vertical panel.

       What is left for a person is the only part a photograph cannot know: how
       many millimetres wide that garment really is.

       Bails and leaves the manual settings alone whenever it cannot be sure —
       a cross-origin photo it may not read, a background it cannot separate,
       or a result outside sane bounds. A wrong automatic measurement would be
       worse than an eyeballed one, because nobody would think to check it. */
    const autoFit = scope.dataset.autoFit === 'true';

    /* How a garment photo sits in the canvas. "cover" crops it to fill, which
       is what every section drew before; "contain" shows the whole photo, the
       way the homepage fit show does, so a square cut-out keeps its sleeves.
       Drawing and auto-fit both go through this one mapping, so the measured
       panel always lands where the photo is actually drawn. */
    const photoFit = scope.dataset.photoFit === 'contain' ? 'contain' : 'cover';
    /* Colour swatches that stay on screen even when a product option also
       names the garment. Asked for on 16 Sep 2026 so the preview can show every
       homepage colour while the order option keeps its own values; an option
       change still moves the preview, a swatch only moves the option when a
       value matches. */
    const swatchesAlways = scope.dataset.garmentSwatches === 'true';
    const gName = q('[data-dtf-fit-garment-name]');
    function photoRect(im) {
      const iw = im.naturalWidth, ih = im.naturalHeight;
      if (photoFit === 'contain') {
        const s = Math.min(W / iw, H / ih);
        return { sx: 0, sy: 0, sw: iw, sh: ih, dx: (W - iw * s) / 2, dy: (H - ih * s) / 2, dw: iw * s, dh: ih * s };
      }
      const ir = iw / ih, cr = W / H;
      let sw = iw, sh = ih, sx = 0, sy = 0;
      if (ir > cr) { sw = sh * cr; sx = (iw - sw) / 2; }
      else { sh = sw / cr; sy = (ih - sh) / 2; }
      return { sx, sy, sw, sh, dx: 0, dy: 0, dw: W, dh: H };
    }

    function measureGarment(gm) {
      const im = gm.img;
      if (!im || !im.complete || !im.naturalWidth || !im.naturalHeight) return null;

      const MW = 260;
      const k = Math.min(1, MW / im.naturalWidth);
      const mw = Math.max(8, Math.round(im.naturalWidth * k));
      const mh = Math.max(8, Math.round(im.naturalHeight * k));
      const c = document.createElement('canvas');
      c.width = mw; c.height = mh;
      let d;
      try {
        const mg = c.getContext('2d', { willReadFrequently: true });
        mg.drawImage(im, 0, 0, mw, mh);
        d = mg.getImageData(0, 0, mw, mh).data;
      } catch (e) { return null; }

      const at = (x, y) => (y * mw + x) * 4;
      // the four corners describe the background, whatever it is
      const corners = [[0,0],[mw-1,0],[0,mh-1],[mw-1,mh-1]].map(([x,y]) => {
        const i = at(x,y); return [d[i], d[i+1], d[i+2], d[i+3]];
      });
      const meanA = corners.reduce((t,c2) => t + c2[3], 0) / 4;
      const useAlpha = meanA < 32;
      const bg = [0,1,2].map(j => corners.reduce((t,c2) => t + c2[j], 0) / 4);
      const isFg = (x, y) => {
        const i = at(x,y);
        if (useAlpha) return d[i+3] > 48;
        if (d[i+3] < 48) return false;
        return Math.abs(d[i]-bg[0]) + Math.abs(d[i+1]-bg[1]) + Math.abs(d[i+2]-bg[2]) > 60;
      };

      let top = -1, bot = -1, left = mw, right = -1;
      for (let y = 0; y < mh; y++) {
        let rowHit = false;
        for (let x = 0; x < mw; x++) {
          if (!isFg(x,y)) continue;
          rowHit = true;
          if (x < left) left = x;
          if (x > right) right = x;
        }
        if (rowHit) { if (top < 0) top = y; bot = y; }
      }
      if (top < 0 || right < left) return null;

      // the body: the widest opaque run on the middle band of the silhouette,
      // sampled over several rows so one stray pixel cannot set the width
      let bL = mw, bR = -1;
      const y0 = Math.round(top + (bot - top) * 0.46);
      const y1 = Math.round(top + (bot - top) * 0.58);
      for (let y = y0; y <= y1; y++) {
        for (let x = 0; x < mw; x++) { if (isFg(x,y)) { if (x < bL) bL = x; break; } }
        for (let x = mw - 1; x >= 0; x--) { if (isFg(x,y)) { if (x > bR) bR = x; break; } }
      }
      if (bR <= bL) return null;

      // back to image pixels, then through the same fit the photo is drawn
      // with, so these land in canvas space
      const r = photoRect(im);
      const toX = v => r.dx + ((v / k) - r.sx) * (r.dw / r.sw);
      const toY = v => r.dy + ((v / k) - r.sy) * (r.dh / r.sh);

      const bodyW = toX(bR + 1) - toX(bL);
      const pct = bodyW / W;
      const cx = (toX(bL) + toX(bR + 1)) / 2 / W;
      const pTop = toY(top) / H;
      const pH = (toY(bot + 1) - toY(top)) / H;

      if (!(pct > 0.12 && pct < 1.02) || !(pH > 0.15 && pH < 1.6)) return null;
      return { pct: pct, cx: cx, top: pTop, hPct: pH };
    }

    function applyAutoFit(gm) {
      if (!autoFit) return;
      const m = measureGarment(gm);
      // A silent fall back to the manual percentages is how a photo ends up
      // scaled by numbers nobody set for it. The editor is told instead.
      gm.autoFail = !m;
      if (!m) return;
      gm.panelPct = m.pct;
      gm.panelTop = m.top;
      gm.panelHPct = m.hPct;
      gm.cx = m.cx;
      gm.ppm = (W * m.pct) / gm.panelW;
      // the print origin follows the measured garment rather than a fixed
      // fraction of the canvas: a correctly measured tee can still put prints
      // on the collar if the origin is left where a different photo put it
      gm.top = m.top + m.hPct * 0.08;
      gm.auto = true;
    }

    // A cross-origin garment photo would taint the canvas and silently break the
    // download, so ask for CORS up front and drop the download if it is refused.
    let tainted = false;
    function loadGarment(gm) {
      if (!gm || gm.img) return;
      const im = new Image();
      gm.img = im;
      im.onload = () => { applyAutoFit(gm); draw(); };
      im.onerror = () => {
        // retry without CORS: the preview still works, the download cannot
        const plain = new Image();
        plain.onload = () => {
          tainted = true;
          if (actions) actions.hidden = true;
          // a tainted canvas cannot be read, so auto-fit cannot run here and
          // the manual numbers stand
          gm.img = plain; draw();
        };
        gm.img = plain; plain.src = gm.src;
      };
      im.crossOrigin = 'anonymous';
      im.src = gm.src;
    }
    garments.forEach(loadGarment);

    /* ---- the customer's artwork ------------------------------------------- */
    let art = null, artUrl = null, artIsVector = false, artBgRemoved = false;
    // where the transfer landed on the canvas, kept so a drag can hit-test it
    let hitBox = null;
    // set once the ordering form is wired; the readout calls it so the cart
    // never lags behind what the canvas is showing
    let orderSync = null;

    function clearArt() {
      if (artUrl) URL.revokeObjectURL(artUrl);
      art = null; artUrl = null; artIsVector = false; artBgRemoved = false;
      if (inFile) inFile.value = '';
      if (fileRow) fileRow.hidden = true;
      if (actions) actions.hidden = true;
      if (drop) drop.classList.remove('is-filled');
      if (thumb) thumb.style.backgroundImage = '';
      if (alphaOut) { alphaOut.hidden = true; alphaOut.textContent = ''; alphaOut.classList.remove('is-warn', 'is-note'); }
      if (btnRmBg) btnRmBg.hidden = true;
      if (dragHint) dragHint.hidden = true;
      if (cv) cv.classList.remove('is-draggable');
      nudgeX = nudgeY = 0;
      seedCustom();
      draw();
    }

    /* Upload problems are said beside the upload, in the status line. The
       readout note was the old home for them, but on the stepped buy box it
       is screen-reader only and sits in a later step, so a rejected file
       there looked like a drop zone that silently refused it. Called after
       clearArt(), which hides the status line. */
    function uploadMsg(msg) {
      if (alphaOut) {
        alphaOut.hidden = false;
        alphaOut.textContent = msg;
        alphaOut.classList.add('is-warn');
        alphaOut.classList.remove('is-note');
      } else if (oNote) {
        oNote.textContent = msg;
      }
    }

    function takeFile(file, bgRemoved) {
      if (!file || !/^image\//.test(file.type)) return;
      if (file.size > 25 * 1024 * 1024) {
        // cleared too, so the oversized file is not left riding on the order
        clearArt();
        uploadMsg('That file is over 25 MB. Export it smaller and try again.');
        return;
      }
      if (artUrl) URL.revokeObjectURL(artUrl);
      artUrl = URL.createObjectURL(file);
      artIsVector = file.type === 'image/svg+xml';
      artBgRemoved = !!bgRemoved;
      const im = new Image();
      im.onload = () => {
        if (!im.naturalWidth || !im.naturalHeight) {
          clearArt();
          uploadMsg('That file has no fixed size. Re-export it with width and height set, or send a PNG.');
          return;
        }
        art = im;
        checkAlpha(im);
        // The custom millimetres are derived from the file's own aspect, so a
        // new file has to re-seed them or the size on screen would belong to
        // the previous upload.
        seedCustom();
        draw();
      };
      im.onerror = () => {
        clearArt();
        uploadMsg('That file could not be read as an image. Upload a PNG, JPG, WEBP or SVG.');
      };
      im.src = artUrl;
      if (fileName) fileName.textContent = file.name;
      if (fileRow) fileRow.hidden = false;
      if (actions) actions.hidden = tainted;
      if (drop) drop.classList.add('is-filled');
      if (thumb) thumb.style.backgroundImage = 'url("' + artUrl + '")';
      if (canDrag || touchDrag) {
        if (dragHint) dragHint.hidden = false;
        cv.classList.add('is-draggable');
      }
      nudgeX = nudgeY = 0;
    }

    if (inFile) inFile.addEventListener('change', e => takeFile(e.target.files && e.target.files[0]));

    /* Remove background, ported from Andre's tool in hw-fitshow: knock out
       everything close to the corner colour. Good for a logo on flat white,
       not a cut-out tool for photographs. Fit show only previews; here the
       file is the order, so the cleaned PNG replaces the one in the form and
       what prints is what the preview shows. If the browser cannot swap the
       file, nothing changes, rather than a clean preview over a dirty order.
       It goes to takeFile directly, not through a change event, because the
       stepped buy box advances a step on change and the customer should see
       the result. */
    /* The thumbnail opens the file larger, on the same checkerboard, because
       32 px is too small to judge a background removal by and transparency
       only shows against a pattern. It reads artUrl, so after Remove
       background it shows the cleaned file, which is the one on the order. */
    const zoomBtn = q('[data-dtf-fit-zoom]'), zoomDlg = q('[data-dtf-fit-zoomdlg]');
    if (zoomBtn && zoomDlg && zoomDlg.showModal) {
      const zoomImg = zoomDlg.querySelector('[data-dtf-fit-zoomimg]');
      const zoomClose = zoomDlg.querySelector('[data-dtf-fit-zoomclose]');
      zoomBtn.addEventListener('click', () => {
        if (!artUrl || !zoomImg) return;
        zoomImg.src = artUrl;
        zoomImg.alt = fileName ? fileName.textContent.trim() : '';
        zoomDlg.showModal();
      });
      if (zoomClose) zoomClose.addEventListener('click', () => zoomDlg.close());
      // a click on the backdrop lands on the dialog element itself
      zoomDlg.addEventListener('click', e => { if (e.target === zoomDlg) zoomDlg.close(); });
    }

    if (btnRmBg && inFile) btnRmBg.addEventListener('click', () => {
      if (!art || artIsVector || btnRmBg.getAttribute('aria-busy') === 'true') return;
      const W = art.naturalWidth, H = art.naturalHeight;
      const fail = msg => { btnRmBg.removeAttribute('aria-busy'); uploadMsg(msg); };
      // 4096 x 4096 is the largest canvas iOS Safari will draw; past it the
      // pixels come back blank instead of throwing
      if (W * H > 16777216) return fail('That file is too large to clean up here. Ask us in the comment box and we will do it.');
      btnRmBg.setAttribute('aria-busy', 'true');
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      try {
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(art, 0, 0);
        const id = g.getImageData(0, 0, W, H), d = id.data;
        const corners = [0, (W - 1) * 4, (H - 1) * W * 4, ((H - 1) * W + W - 1) * 4];
        let r = 0, gg = 0, b = 0;
        corners.forEach(i => { r += d[i]; gg += d[i + 1]; b += d[i + 2]; });
        r /= 4; gg /= 4; b /= 4;
        const TOL = 48;
        for (let i = 0; i < d.length; i += 4) {
          const dist = Math.abs(d[i] - r) + Math.abs(d[i + 1] - gg) + Math.abs(d[i + 2] - b);
          if (dist < TOL) d[i + 3] = 0;
          else if (dist < TOL * 2) d[i + 3] = Math.round(d[i + 3] * (dist - TOL) / TOL);
        }
        g.putImageData(id, 0, 0);
      } catch (e) {
        return fail('That file could not be cleaned up here. Ask us in the comment box and we will do it.');
      }
      c.toBlob(blob => {
        if (!blob) return fail('That file could not be cleaned up here. Ask us in the comment box and we will do it.');
        const base = ((fileName && fileName.textContent.trim()) || 'artwork').replace(/\.[^.]+$/, '');
        const f = new File([blob], base + '-no-background.png', { type: 'image/png' });
        try {
          const dt = new DataTransfer();
          dt.items.add(f);
          inFile.files = dt.files;
        } catch (e) {
          return fail('This browser cannot swap the file. Remove the background before uploading, or ask us in the comment box.');
        }
        btnRmBg.removeAttribute('aria-busy');
        takeFile(f, true);
      }, 'image/png');
    });
    if (btnClear) btnClear.addEventListener('click', clearArt);
    if (drop) {
      ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, e => {
        e.preventDefault(); drop.classList.add('is-over');
      }));
      ['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, e => {
        e.preventDefault(); drop.classList.remove('is-over');
      }));
      /* Cancelling the drop (above) also cancels the browser putting the file
         into the input, so it is put there by hand. Without this a dropped
         file showed in the preview and never reached the order. It goes
         through a change event so a drop behaves exactly like Choose file,
         including the stepped buy box's advance. A browser that cannot set
         the input gets told to use the button rather than shown a preview
         the order will not carry. */
      drop.addEventListener('drop', e => {
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        // An image dragged from a web page or a mail app usually arrives as a
        // link, not a file. Say so, rather than letting the drop vanish.
        if (!f) {
          uploadMsg('That drop did not include a file. Save the image to your computer first, then drag it in or use Choose file.');
          return;
        }
        if (!/^image\//.test(f.type)) {
          uploadMsg('That file type cannot be used here. Upload a PNG, JPG, WEBP or SVG.');
          return;
        }
        if (!inFile) { takeFile(f); return; }
        try {
          const dt = new DataTransfer();
          dt.items.add(f);
          inFile.files = dt.files;
        } catch (err) {
          uploadMsg('This browser cannot take a dropped file. Use the Choose file button instead.');
          return;
        }
        inFile.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (btnRuler) {
      btnRuler.addEventListener('click', () => {
        showRuler = !showRuler;
        btnRuler.classList.toggle('is-off', !showRuler);
        // pressed describes the state the control has put the page in, so it
        // is true when the ruler is hidden, matching the label the button then
        // offers to undo
        btnRuler.setAttribute('aria-pressed', showRuler ? 'false' : 'true');
        draw();
      });
    }

    /* Both export paths capture the canvas bitmap synchronously — toBlob
       snapshots when it is called, not when its callback runs — so drawing
       without chrome around the call is enough, and the visible canvas is put
       back before anything can be seen mid-export. Sections with no ruler
       toggle export exactly as they did before. */
    function exportPass(fn) {
      if (!btnRuler && !rulerAuto) return fn();
      exporting = true;
      draw();
      try { fn(); } finally { exporting = false; draw(); }
    }

    if (btnDl) btnDl.addEventListener('click', () => {
      if (tainted || !cv.toBlob) return;
      exportPass(() => cv.toBlob(blob => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const slug = t => (t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        a.download = ['fitcheck', slug(cur.label) || 'transfer', place ? slug(place.label) : '']
          .filter(Boolean).join('-') + '.png';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }, 'image/png'));
    });

    // relative luminance, so "is this ground light" is measured rather than
    // guessed from the hex looking pale
    function isLight(hex) {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
      if (!m) return false;
      const n = parseInt(m[1], 16);
      const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return (0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2]) > 0.35;
    }

    /* ---- drawing ----------------------------------------------------------- */
    function drawPhotoBg(im) {
      const r = photoRect(im);
      g.drawImage(im, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy, r.dw, r.dh);
    }

    // the artwork keeps its aspect ratio inside the ordered size: the transfer
    // is a box you buy, the logo sits within it
    function contain(iw, ih, bw, bh) {
      const s = Math.min(bw / iw, bh / ih);
      const w = iw * s, h = ih * s;
      return { x: (bw - w) / 2, y: (bh - h) / 2, w, h };
    }

    /* The canvas ground. It was hardcoded navy, which is right behind FitCheck's
       dark instrument panel and wrong twice over here: the buy box sits on a
       light panel, and a black garment on navy is very nearly invisible. So the
       garment names its own ground, falling back to the section's and then to
       the original navy, which leaves every existing section drawing exactly
       what it drew before. A garment shot on transparency needs a ground that
       opposes it, and black and white tees do not want the same one. */
    const stageBg = scope.dataset.stageBg || '#051133';

    function draw() {
      const im = garment && garment.img;
      const usePhoto = im && im.complete && im.naturalWidth > 0;
      g.fillStyle = (garment && garment.bg) || stageBg; g.fillRect(0, 0, W, H);

      let sc, px, py, pw, ph;
      if (usePhoto) {
        drawPhotoBg(im);
        sc = garment.ppm;
        pw = cur.w * sc; ph = cur.h * sc;
        px = W * garment.cx - pw / 2; py = H * garment.top;
      } else {
        // headroom at the top only, for the size label above the print area
        const padX = 26, padTop = 54, padBot = 26;
        sc = Math.min((W - padX * 2) / TEE_W, (H - padTop - padBot) / TEE_H);
        const ox = (W - TEE_W * sc) / 2, oy = padTop + (H - padTop - padBot - TEE_H * sc) / 2;
        // the placeholder tee is drawn in navy ink on a dark ground; on a light
        // one it needs the inverse or it disappears along with the garment
        const lightGround = isLight((garment && garment.bg) || stageBg);
        drawTee(g, ox, oy, TEE_W * sc, TEE_H * sc,
          lightGround ? '#E4E7F1' : '#0E1A4E',
          lightGround ? 'rgba(2,11,65,.28)' : 'rgba(255,255,255,.22)');
        pw = cur.w * sc; ph = cur.h * sc;
        px = ox + (TEE_W * sc - pw) / 2; py = oy + (70 * sc) + (TEE_H * sc * .06);
      }

      /* Placement and the free-hand nudge are both millimetres off the garment's
         own print origin, so they mean the same thing on a tee and on a cap. The
         clamp is folded back into the nudge rather than applied on top of it:
         otherwise dragging past the edge banks up an invisible offset that snaps
         the artwork sideways the next time the size changes. */
      const wantX = px + ((place ? place.dx : 0) + nudgeX) * sc;
      const wantY = py + ((place ? place.dy : 0) + DROP + nudgeY) * sc;
      px = pw < W ? Math.max(0, Math.min(W - pw, wantX)) : wantX;
      py = ph < H ? Math.max(0, Math.min(H - ph, wantY)) : wantY;
      nudgeX += (px - wantX) / sc;
      nudgeY += (py - wantY) / sc;
      hitBox = { x: px, y: py, w: pw, h: ph, sc: sc };

      // the ordered size: the thing being bought, drawn unless the ruler is
      // off or this pass is an export
      const chrome = showRuler && !exporting && !(rulerAuto && art);
      if (chrome) {
        g.fillStyle = art ? 'rgba(253,99,0,.06)' : 'rgba(253,99,0,.16)';
        g.fillRect(px, py, pw, ph);
      }

      let artMm = 0;
      if (art) {
        const box = contain(art.naturalWidth, art.naturalHeight, pw, ph);
        g.drawImage(art, px + box.x, py + box.y, box.w, box.h);
        artMm = box.w / sc;
      }

      if (chrome) {
        g.strokeStyle = FLAME; g.lineWidth = 2.5; g.setLineDash([8, 6]);
        g.strokeRect(px, py, pw, ph); g.setLineDash([]);
        if (!art && !usePhoto) drawMark(g, px + pw * .11, py + ph * .19, pw * .78, ph * .62, 1, FLAME);
      }

      // calibration guide, theme editor only: match the dashed span to the
      // garment's printable panel and the millimetre settings do the rest
      if (editing && usePhoto && garment.panelPct) {
        const gw = W * garment.panelPct, gx = W * garment.cx - gw / 2;
        const gy = garment.panelTop != null ? H * garment.panelTop : py - 18;
        // the guide has to read on whatever ground the garment sits on, so it
        // takes the ink the ground can carry rather than always white
        const guideInk = isLight((garment && garment.bg) || stageBg)
          ? 'rgba(2,11,65,.85)' : 'rgba(255,255,255,.8)';
        g.strokeStyle = guideInk; g.lineWidth = 2;
        g.font = '600 15px ui-monospace,monospace'; g.fillStyle = guideInk;

        g.setLineDash([5, 5]);
        g.beginPath(); g.moveTo(gx, gy); g.lineTo(gx + gw, gy); g.stroke();
        g.setLineDash([]);
        g.beginPath(); g.moveTo(gx, gy - 7); g.lineTo(gx, gy + 7);
        g.moveTo(gx + gw, gy - 7); g.lineTo(gx + gw, gy + 7); g.stroke();
        g.textAlign = 'center'; g.fillText('panel ' + fmtLen(garment.panelW) + (garment.auto ? ' (measured)' : ''), gx + gw / 2, gy - 12);
        g.textAlign = 'left';

        /* The vertical span is measured as well as the width so the editor can
           answer a question the width alone cannot: is this photo actually in
           proportion? A mockup that is longer than a real garment still gets a
           correct preview, because one uniform scale trued to chest width keeps
           the customer's artwork undistorted — but the merchant should be told,
           not left to wonder why the shirt looks long. */
        if (garment.panelHPct) {
          const gh = H * garment.panelHPct;
          g.setLineDash([5, 5]);
          g.beginPath(); g.moveTo(gx, gy); g.lineTo(gx, gy + gh); g.stroke();
          g.setLineDash([]);
          g.beginPath(); g.moveTo(gx - 7, gy + gh); g.lineTo(gx + 7, gy + gh); g.stroke();
          g.fillText(fmtLen(garment.panelH), gx + 10, gy + gh / 2);

          const ppmY = gh / garment.panelH, skew = ppmY / garment.ppm - 1;
          if (Math.abs(skew) > .03) {
            const pct = Math.round(Math.abs(skew) * 100);
            g.font = '600 15px ui-monospace,monospace';
            g.fillStyle = FLAME; g.textAlign = 'center';
            g.fillText('photo is ' + pct + '% ' + (skew > 0 ? 'long' : 'short') +
              ' — preview trued to width', W / 2, H - 16);
            g.textAlign = 'left';
          }
        }
      }

      if (chrome) {
        if (editing && garment && garment.autoFail) {
        g.font = '600 15px ui-monospace,monospace'; g.fillStyle = FLAME;
        g.textAlign = 'center';
        g.fillText('could not find the garment against its background', W / 2, 22);
        g.fillText('— using this block\u2019s manual percentages', W / 2, 42);
        g.textAlign = 'left';
      }

      g.font = '600 20px ui-monospace,monospace'; g.fillStyle = FLAME; g.textAlign = 'center';
        const labelY = usePhoto ? Math.min(H - 12, py + ph + 26) : Math.max(28, py - 16);
        g.fillText(fmtPair(cur.w, cur.h), px + pw / 2, labelY);
        g.textAlign = 'left';
      }

      readout(artMm);
    }

    function readout(artMm) {
      readoutBody(artMm);
      if (orderSync) orderSync();
    }

    function readoutBody(artMm) {
      const panelW = garment ? garment.panelW : TEE_W;
      const panelH = garment ? garment.panelH : TEE_H;
      if (capOut && place) capOut.textContent = place.label;
      if (gName && garment && gName.textContent !== garment.label) gName.textContent = garment.label;
      if (oSize) oSize.textContent = fmtPair(cur.w, cur.h);
      if (oCov) oCov.textContent = Math.round((cur.w * cur.h) / (panelW * panelH) * 100) + '%';
      if (oPr) oPr.innerHTML = cur.price ? cur.price : '<span class="dtf-tbc">TBC</span>';

      const tooBig = cur.w > panelW || cur.h > panelH;
      const gNote = (tooBig ? ' ' + MSG.big : '') +
                    (garment && garment.note ? ' ' + garment.note : '') +
                    (place && place.note ? ' ' + place.note : '');

      if (!art) {
        if (oDpi) { oDpi.innerHTML = '<span class="dtf-tbc">&mdash;</span>'; oDpi.classList.remove('is-fail'); }
        if (oNote) oNote.textContent = (cur.price
          ? cur.label + ' is a published size on our pricing page.'
          : cur.label + ' pricing depends on how it nests onto a sheet — the builder works it out live.') + gNote;
        return;
      }

      if (artIsVector) {
        if (oDpi) { oDpi.innerHTML = 'Vector'; oDpi.classList.remove('is-fail'); }
        if (oNote) oNote.textContent = 'Vector artwork has no fixed resolution — it stays sharp at any transfer size.' + gNote;
        return;
      }

      // Same arithmetic and the same rounded comparison as the resolution
      // gauge, so the two instruments can never contradict each other.
      const dpi = artMm > 0 ? art.naturalWidth / (artMm / 25.4) : 0;
      const shown = Math.round(dpi);
      const maxW = (art.naturalWidth / TARGET) * 25.4;
      const needed = Math.ceil((artMm / 25.4) * TARGET);

      if (oDpi) {
        oDpi.innerHTML = shown + '<small>dpi</small>';
        oDpi.classList.toggle('is-fail', shown < FLOOR);
      }
      if (!oNote) return;
      if (shown >= TARGET) {
        oNote.innerHTML = MSG.good + '. At ' + Math.round(artMm) + ' mm wide your file gives <strong>' +
          shown + ' dpi</strong>, at or above the ' + TARGET + ' dpi we print to.' + gNote;
      } else if (shown >= FLOOR) {
        oNote.innerHTML = MSG.ok + '. This lands at <strong>' + shown + ' dpi</strong>. It will print, but fine detail and edges will soften. For ' +
          TARGET + ' dpi at this size you would need a ' + needed.toLocaleString('en-GB') +
          ' px wide file, or print it at ' + Math.round(maxW) + ' mm instead.' + gNote;
      } else {
        oNote.innerHTML = MSG.bad + '. <strong>' + shown + ' dpi</strong> is below the ' + FLOOR +
          ' dpi floor. Enlarging a small file does not add detail — you need it re-exported at ' +
          needed.toLocaleString('en-GB') + ' px wide, or printed no wider than ' + Math.round(maxW) + ' mm.' + gNote;
      }
    }

    /* ---- choosers ----------------------------------------------------------
       Two renderings of one chooser. A row of chips is right when the options
       are few and comparing them matters (garment, placement). A select is
       right when the list is long and each option is a code the customer is
       looking up rather than weighing — Supacolour's price code is twenty-odd
       rows, and twenty chips is a wall. The host element decides: pass a
       <select> and it fills options, pass a div and it fills chips. Both call
       the same onPick, so the canvas and the cart cannot tell them apart.

       `item.opt` is the long human label ("WE_XS  XS  40 x 40 mm"); `item.label`
       stays the variant-matching key, so widening what the select says can
       never break findVariant(). */
    function chips(host, items, isOn, onPick) {
      if (host.tagName === 'SELECT') {
        host.textContent = '';
        items.forEach((item, i) => {
          const o = document.createElement('option');
          o.value = String(i);
          o.textContent = item.opt || item.label;
          if (isOn(item, i)) o.selected = true;
          host.appendChild(o);
        });
        host.addEventListener('change', () => {
          const item = items[parseInt(host.value, 10)];
          if (!item) return;
          onPick(item);
          draw();
        });
        return null;
      }
      host.textContent = '';
      const made = [];
      items.forEach((item, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dtf-size' + (item.hex ? ' dtf-size--swatch' : '') + (isOn(item, i) ? ' is-on' : '');
        b.setAttribute('aria-pressed', isOn(item, i) ? 'true' : 'false');
        if (item.hex) {
          // a colour chip is the colour, named for assistive tech and on hover
          b.style.setProperty('--sw', item.hex);
          b.setAttribute('aria-label', item.label);
          b.title = item.label;
        } else {
          b.textContent = item.label;
        }
        b.addEventListener('click', () => {
          onPick(item);
          host.querySelectorAll('.dtf-size').forEach(x => {
            const on = x === b;
            x.classList.toggle('is-on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          draw();
        });
        host.appendChild(b);
        made.push({ el: b, item: item });
      });
      return made;
    }

    let gBtns = null;
    if (gUi && gRow && garments.length > 1) {
      gRow.hidden = false;
      gBtns = chips(gUi, garments, gm => gm === garment, gm => {
        garment = gm; loadGarment(gm); pushGarmentToOption(gm);
      });
    }

    /* ---- garment colour and the variant option ----------------------------
       The product sells a black or a white transfer backing, and the customer
       also picks a garment to see it on. Those were two controls saying the
       same word, and nothing stopped the picture showing black while the order
       said white. So the option drives the preview and the preview drives the
       option, in both directions, and the two can no longer disagree.

       Matching is by the garment's stated option value first and its label
       second, so "White T-shirt" still finds the option value "White" without
       the merchant having to name it. */
    const gOpt = scope.querySelector('[data-dtf-fit-garment-opt]');
    let syncingGarment = false;

    const normv = t => String(t == null ? '' : t).trim().toLowerCase();

    function garmentForValue(v) {
      const n = normv(v);
      if (!n) return null;
      return garments.find(g => g.opt && normv(g.opt) === n)
          || garments.find(g => normv(g.label) === n)
          || garments.find(g => normv(g.label).indexOf(n) >= 0)
          || null;
    }

    function selectGarment(gm) {
      if (!gm || gm === garment) return;
      garment = gm;
      loadGarment(gm);
      if (gBtns) gBtns.forEach(b => {
        const on = b.item === gm;
        b.el.classList.toggle('is-on', on);
        b.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      draw();
    }

    function pushGarmentToOption(gm) {
      if (!gOpt || syncingGarment) return;
      const want = normv(gm.opt) || normv(gm.label);
      const match = Array.prototype.slice.call(gOpt.options)
        .find(o => normv(o.value) === want || want.indexOf(normv(o.value)) >= 0);
      if (!match || gOpt.value === match.value) return;
      syncingGarment = true;
      gOpt.value = match.value;
      // the variant lookup and the running price listen for change, so this has
      // to be a real event rather than a silent assignment
      gOpt.dispatchEvent(new Event('change', { bubbles: true }));
      syncingGarment = false;
    }

    if (gOpt) {
      /* Two controls for one choice is one too many. When a product option is
         driving the garment, the swatch row under the preview is saying the
         same thing a second time, so it goes — and it comes back on its own
         wherever no option is driving it, such as a page that sells this
         without that option. Nothing to configure either way. */
      if (gRow && !swatchesAlways) gRow.hidden = true;

      gOpt.addEventListener('change', () => {
        if (syncingGarment) return;
        syncingGarment = true;
        selectGarment(garmentForValue(gOpt.value));
        syncingGarment = false;
      });
      // and once at load, so the picture agrees with the option the page opened
      // on. The chips were already built against the first garment, so the
      // highlight moves too rather than pointing at the wrong swatch.
      const initial = garmentForValue(gOpt.value);
      if (initial && initial !== garment) {
        garment = initial;
        loadGarment(initial);
        if (gBtns) gBtns.forEach(b => {
          const on = b.item === initial;
          b.el.classList.toggle('is-on', on);
          b.el.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }
    /* `cur` is a copy, not the block itself: the custom-size inputs below write
       real millimetres onto it, and mutating the parsed size list would make a
       code permanently mean whatever the last visitor typed. `label` is left
       alone because findVariant() matches on it. */
    let baseSize = sizes[start];
    chips(wrap, sizes, (sz, i) => i === start, sz => {
      baseSize = sz; cur = Object.assign({}, sz); seedCustom();
      // a size can name the placement it belongs at — Pocket on the left chest,
      // the others centred — so the guide lands where the print really goes
      if (sz.place && pUi) {
        const want = String(sz.place).trim().toLowerCase();
        const pl = placements.find(p => String(p.label).trim().toLowerCase() === want);
        if (pl && pl !== place) {
          place = pl; nudgeX = nudgeY = 0;
          pUi.querySelectorAll('.dtf-size').forEach((b, i) => {
            const on = placements[i] === pl;
            b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
      }
    });

    /* ---- custom size -------------------------------------------------------
       The price code names a box; the transfer is the artwork fitted inside it.
       So the millimetres are seeded by containing the uploaded file's own
       aspect in the code's box — a square code and a wide logo do not produce a
       square print — and stay editable, because a customer who has measured the
       garment knows better than the default. Nothing here changes which variant
       is bought: the code does that, and this is the size printed at. */
    const inW = scope.querySelector('[data-dtf-fit-w]');
    const inH = scope.querySelector('[data-dtf-fit-h]');
    const rngW = scope.querySelector('[data-dtf-fit-w-range]');
    const rngH = scope.querySelector('[data-dtf-fit-h-range]');
    const customNote = scope.querySelector('[data-dtf-fit-customnote]');

    /* The slider's ceiling is the garment, not a round number: the largest
       print you can sell is the one that fits the panel, so dragging to the end
       lands on the biggest real option rather than somewhere the press cannot
       go. It moves when the garment does, because a cap and a tee do not have
       the same ceiling. */
    function syncRangeBounds() {
      const maxW = Math.round(garment ? garment.panelW : TEE_W);
      const maxH = Math.round(garment ? garment.panelH : TEE_H);
      if (rngW) rngW.max = String(maxW);
      if (rngH) rngH.max = String(maxH);
      if (inW) inW.max = String(maxW);
      if (inH) inH.max = String(maxH);
    }

    function seedCustom() {
      if (!inW || !inH || !baseSize) return;
      let w = baseSize.w, h = baseSize.h;
      if (art && art.naturalWidth && art.naturalHeight) {
        const f = contain(art.naturalWidth, art.naturalHeight, baseSize.w, baseSize.h);
        w = f.w; h = f.h;
      }
      cur.w = Math.round(w * 10) / 10;
      cur.h = Math.round(h * 10) / 10;
      inW.value = Math.round(cur.w / uf * 10) / 10;
      inH.value = Math.round(cur.h / uf * 10) / 10;
      syncRangeBounds();
      if (rngW) rngW.value = String(Math.round(cur.w));
      if (rngH) rngH.value = String(Math.round(cur.h));
      if (customNote) customNote.hidden = !art;
      draw();
    }

    function readCustom() {
      if (!inW || !inH) return;
      const w = parseFloat(inW.value) * uf, h = parseFloat(inH.value) * uf;
      if (isFinite(w) && w > 0) cur.w = Math.round(w * 10) / 10;
      if (isFinite(h) && h > 0) cur.h = Math.round(h * 10) / 10;
      // the slider follows the number without firing its own handler back
      if (rngW) rngW.value = String(Math.round(cur.w));
      if (rngH) rngH.value = String(Math.round(cur.h));
      draw();
    }

    function readRange() {
      if (!inW || !inH) return;
      if (rngW) { cur.w = parseFloat(rngW.value); inW.value = Math.round(cur.w / uf * 10) / 10; }
      if (rngH) { cur.h = parseFloat(rngH.value); inH.value = Math.round(cur.h / uf * 10) / 10; }
      draw();
    }

    if (inW && inH) {
      [inW, inH].forEach(el => {
        el.addEventListener('change', readCustom);
        el.addEventListener('input', readCustom);
      });
      [rngW, rngH].forEach(el => {
        if (!el) return;
        el.addEventListener('input', readRange);
        el.addEventListener('change', readRange);
      });
      scope.querySelectorAll('[data-dtf-fit-dim-step]').forEach(btn => {
        btn.addEventListener('click', () => {
          const target = btn.dataset.dtfFitDimStep === 'w' ? inW : inH;
          const by = parseFloat(btn.dataset.dtfFitDimBy) || 1;
          target.value = Math.max(1, Math.round(((parseFloat(target.value) || 0) + by) * 10) / 10);
          readCustom();
        });
      });
      seedCustom();
    }
    if (pUi && pRow && placements.length > 1) {
      pRow.hidden = false;
      // picking a named placement discards the free-hand nudge: the chip is a
      // statement about where the print goes, not a starting point to correct
      chips(pUi, placements, pl => pl === place, pl => { place = pl; nudgeX = nudgeY = 0; });
    }

    /* ---- ordering ----------------------------------------------------------
       The preview and the order have to agree, so the variant, the properties
       and the gate are all recomputed from the same state the canvas draws
       from. The button starts disabled in the markup and is only ever enabled
       here: an order that reached production without a variant id or without
       the size and placement would be worse than no order. */
    const form = scope.querySelector('[data-dtf-fit-form]');
    if (form) {
      const variants = parse(scope.dataset.dtfFitVariants, []);
      const sizeIdx = parseInt(scope.dataset.sizeOptionIndex, 10);
      const vIn = form.querySelector('[data-dtf-fit-variant]');
      const btn = form.querySelector('[data-dtf-fit-add]');
      const gate = form.querySelector('[data-dtf-fit-gate]');
      const opts = Array.prototype.slice.call(form.querySelectorAll('[data-dtf-fit-opt]'));
      const props = {};
      form.querySelectorAll('[data-dtf-fit-prop]').forEach(el => { props[el.dataset.dtfFitProp] = el; });
      const blockLow = scope.dataset.blockLowDpi === 'true';
      const GATE = {
        art:  scope.dataset.msgNeedArt   || 'Upload your artwork to order.',
        dpi:  scope.dataset.msgBlockDpi  || 'That file is too low resolution to print at this size.',
        gone: scope.dataset.msgSoldOut   || 'That combination is sold out.',
        min:  scope.dataset.msgMinQty    || 'Add at least {min} across the sizes to order.'
      };

      const norm = t => String(t == null ? '' : t).trim().toLowerCase();

      function findVariant() {
        const want = [];
        opts.forEach(sel => { want[parseInt(sel.dataset.dtfFitOpt, 10)] = norm(sel.value); });
        want[sizeIdx] = norm(cur.label);
        return variants.find(v => v.opts.every((o, i) => norm(o) === want[i])) || null;
      }

      // dpi is recomputed rather than scraped off the readout: the readout is
      // for a person, this decides whether a job can be printed
      function artDpi() {
        if (!art || artIsVector || !hitBox) return null;
        const fit = contain(art.naturalWidth, art.naturalHeight, hitBox.w, hitBox.h);
        const mm = fit.w / hitBox.sc;
        return mm > 0 ? Math.round(art.naturalWidth / (mm / 25.4)) : null;
      }

      function syncOrder() {
        const v = findVariant();
        const dpi = artDpi();
        let stop = '';
        if (!art) stop = GATE.art;
        else if (bdIns.length && bdSum() < minQty) stop = GATE.min.replace('{min}', minQty);
        else if (blockLow && dpi != null && dpi < FLOOR) stop = GATE.dpi;
        else if (!v) stop = GATE.gone;
        else if (!v.available) stop = GATE.gone;

        if (vIn) vIn.value = v ? v.id : '';
        if (props.size) props.size.value = cur.label + ' (' + cur.w + ' × ' + cur.h + ' mm)';
        if (props.placement) props.placement.value = place ? place.label : '';
        if (props.garment) props.garment.value = garment && garment.label ? garment.label : '';
        if (props.dpi) props.dpi.value = artIsVector ? 'Vector' : (dpi == null ? '' : dpi + ' dpi');

        if (btn) btn.disabled = !!stop;
        if (gate) gate.textContent = stop;
        syncQty();
        // price on the readout comes from the variant once this is orderable,
        // so the rate card and the checkout cannot disagree
        if (oPr && v) oPr.textContent = v.price;
      }

      /* ---- quantity and volume tiers -------------------------------------
         The tier table can run ahead of the discount that makes it true, so
         the running total follows the tier price only when the merchant has
         confirmed the discount exists. Until then it follows the variant
         price, which is what the cart will actually charge. A total that
         disagrees with checkout is not a rounding error, it is a wrong price
         on screen. */
      const qtyIn = form.querySelector('[data-dtf-fit-qty]');
      const eachOut = form.querySelector('[data-dtf-fit-each]');
      const totalOut = form.querySelector('[data-dtf-fit-total]');
      const tierWrap = form.querySelector('[data-dtf-fit-tiers]');
      const tiers = tierWrap ? parse(tierWrap.dataset.dtfFitTiers, []) : [];
      const tiersLive = !!(tierWrap && tierWrap.dataset.tiersActive === 'true');
      /* Percentage tiers. Shopify's automatic discounts take a percentage off
         the variant price, so a fixed rand price per tier is only right for the
         one size it was worked out on; an A3 at 20+ showed the A4 rate. A
         section that opts in prices each tier as variant × (1 − its "% off"),
         the same sum checkout does, and writes the table from the variant on
         screen. Sections that don't opt in keep their fixed tier prices. */
      const tierPct = scope.dataset.tierPercent === 'true';
      const pctOf = t => { const m = /(\d+(?:[.,]\d+)?)\s*%/.exec(t && t.off || ''); return m ? parseFloat(m[1].replace(',', '.')) : 0; };
      /* Checkout rounds the discount per unit DOWN to the cent and multiplies
         by the quantity: 250 × A4 at 21% showed R29.69 each and R1,970.00 off
         (16 Sep 2026), where a plain round gave R29.68. Worked in cents so the
         page lands on the same figures. */
      const tierEachC = (price, pct) => {
        const c = Math.round(price * 100);
        return c - Math.floor(c * pct / 100 + 1e-6);
      };
      const minQty = qtyIn ? (parseInt(qtyIn.min, 10) || 1) : 1;

      /* Size breakdown. One print, several garment sizes: 20 M, 15 S and 15 L
         is one order of 50. All sizes cost the same, so it goes to the cart as
         ONE line of the total with the split as a property — the artwork
         uploads once, and a single line of 50 meets a "minimum 50 items"
         discount without depending on how Shopify counts across lines.
         The total is never clamped up to the minimum here: the gate says
         what is missing instead, so the split and the quantity can't disagree. */
      const bdIns = Array.prototype.slice.call(form.querySelectorAll('[data-dtf-fit-bd]'));
      const bdTotal = form.querySelector('[data-dtf-fit-bd-total]');
      const bdSum = () => bdIns.reduce((t, el) => {
        const n = parseInt(el.value, 10); return t + (isFinite(n) && n > 0 ? n : 0);
      }, 0);
      const bdText = () => bdIns.map(el => {
        const n = parseInt(el.value, 10); return isFinite(n) && n > 0 ? el.dataset.dtfFitBd + ' ' + n : '';
      }).filter(Boolean).join(', ');

      // "R48.00" and "R1 234,56" both have to survive the trip to a number
      const toNum = t => {
        const m = String(t == null ? '' : t).replace(/[^\d.,]/g, '').replace(/\s/g, '');
        const cleaned = m.lastIndexOf(',') > m.lastIndexOf('.')
          ? m.replace(/\./g, '').replace(',', '.')
          : m.replace(/,/g, '');
        const n = parseFloat(cleaned);
        return isFinite(n) ? n : null;
      };
      const fmtLike = (sample, n) => {
        const sym = String(sample || '').replace(/[\d.,\s]/g, '') || '';
        return sym + ' ' + n.toFixed(2);
      };

      function tierFor(q) {
        let hit = null;
        tiers.forEach(t => {
          const min = parseInt(t.min, 10);
          if (isFinite(min) && q >= min && (!hit || min > parseInt(hit.min, 10))) hit = t;
        });
        return hit;
      }

      function syncQty() {
        if (!qtyIn) return;
        let q;
        if (bdIns.length) {
          q = bdSum();
          qtyIn.value = q;
          if (bdTotal && bdTotal.textContent !== String(q)) bdTotal.textContent = String(q);
          if (props.sizes) props.sizes.value = bdText();
        } else {
          q = parseInt(qtyIn.value, 10);
          if (!isFinite(q) || q < minQty) q = minQty;
          qtyIn.value = q;
        }

        const t = tierFor(q);
        if (tierWrap) {
          tierWrap.querySelectorAll('[data-dtf-fit-tier-row]').forEach(row => {
            row.classList.toggle('is-on', !!t && row.dataset.dtfFitTierRow === String(t.min));
          });
        }

        const v = findVariant();
        const live = v ? toNum(v.price) : null;
        const sample = (v && v.price) || (t && t.each) || '';

        if (tierPct && live != null && tierWrap) {
          tierWrap.querySelectorAll('[data-dtf-fit-tier-row]').forEach(row => {
            const cell = row.querySelector('[data-dtf-fit-tier-each]');
            const tr = tiers.find(x => String(x.min) === row.dataset.dtfFitTierRow);
            if (cell && tr) {
              const txt = fmtLike(sample, tierEachC(live, pctOf(tr)) / 100);
              if (cell.textContent !== txt) cell.textContent = txt;
            }
          });
        }

        if (tierPct && tiersLive && t && live != null) {
          const eachC = tierEachC(live, pctOf(t));
          if (eachOut) eachOut.textContent = fmtLike(sample, eachC / 100) + ' /ea';
          if (totalOut) totalOut.textContent = fmtLike(sample, eachC * q / 100);
          return;
        }
        const each = tiersLive && t ? toNum(t.each) : live;
        if (eachOut) eachOut.textContent = each == null ? '' : fmtLike(sample, each) + ' /ea';
        if (totalOut) totalOut.textContent = each == null ? '' : fmtLike(sample, each * q);
      }

      if (qtyIn) {
        qtyIn.addEventListener('change', syncQty);
        qtyIn.addEventListener('input', syncQty);
        const step = (by) => {
          qtyIn.value = (parseInt(qtyIn.value, 10) || minQty) + by;
          syncQty();
        };
        const up = form.querySelector('[data-dtf-fit-qty-up]');
        const down = form.querySelector('[data-dtf-fit-qty-down]');
        if (up) up.addEventListener('click', () => step(1));
        if (down) down.addEventListener('click', () => step(-1));
      }

      opts.forEach(sel => sel.addEventListener('change', syncQty));
      bdIns.forEach(el => {
        el.addEventListener('input', syncOrder);
        el.addEventListener('change', () => {
          const n = parseInt(el.value, 10);
          el.value = isFinite(n) && n > 0 ? n : 0;
          syncOrder();
        });
      });
      form.querySelectorAll('[data-dtf-fit-bd-step]').forEach(b => {
        b.addEventListener('click', () => {
          const el = document.getElementById(b.getAttribute('aria-controls'));
          if (!el) return;
          el.value = Math.max(0, (parseInt(el.value, 10) || 0) + (parseInt(b.dataset.dtfFitBdStep, 10) || 0));
          // bubbles so the step chrome's summary hears it too
          el.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
      opts.forEach(sel => sel.addEventListener('change', syncOrder));

      /* The mockup is composed at submit rather than kept in step on every
         redraw: it costs a full canvas encode, and the only version that
         matters is the one on screen when they committed. A tainted canvas
         (garment photo refused CORS) cannot be read at all, so the order goes
         without it rather than not going. */
      const previewIn = form.querySelector('[data-dtf-fit-preview]');
      let composed = false;
      if (previewIn) {
        form.addEventListener('submit', e => {
          if (composed || !art || tainted || !cv.toBlob) return;
          e.preventDefault();
          let sent = false;
          const go = () => {
            if (sent) return;
            sent = composed = true;
            form.submit();
          };
          // never let a stalled encode swallow the order
          setTimeout(go, 4000);
          exportPass(() => cv.toBlob(blob => {
            if (blob) {
              try {
                const dt = new DataTransfer();
                dt.items.add(new File([blob], 'fitcheck-preview.png', { type: 'image/png' }));
                previewIn.files = dt.files;
              } catch (err) { /* no DataTransfer: order still goes, without the mockup */ }
            }
            go();
          }, 'image/png'));
        });
      }

      orderSync = syncOrder;
      syncOrder();
    }

    /* ---- drag to nudge ----------------------------------------------------- */
    if (canDrag) {
      let dragging = false, lastX = 0, lastY = 0;
      const at = e => {
        const r = cv.getBoundingClientRect();
        if (!r.width || !r.height) return { x: 0, y: 0 };
        return { x: (e.clientX - r.left) * (W / r.width), y: (e.clientY - r.top) * (H / r.height) };
      };
      cv.addEventListener('pointerdown', e => {
        if (!art || !hitBox || e.button !== 0 || e.pointerType === 'touch') return;
        const p = at(e);
        if (p.x < hitBox.x || p.x > hitBox.x + hitBox.w ||
            p.y < hitBox.y || p.y > hitBox.y + hitBox.h) return;
        dragging = true; lastX = p.x; lastY = p.y;
        cv.classList.add('is-dragging');
        cv.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      cv.addEventListener('pointermove', e => {
        if (!dragging || !hitBox) return;
        const p = at(e);
        nudgeX += (p.x - lastX) / hitBox.sc;
        nudgeY += (p.y - lastY) / hitBox.sc;
        lastX = p.x; lastY = p.y;
        draw();
      });
      const endDrag = e => {
        if (!dragging) return;
        dragging = false;
        cv.classList.remove('is-dragging');
        if (cv.hasPointerCapture(e.pointerId)) cv.releasePointerCapture(e.pointerId);
      };
      cv.addEventListener('pointerup', endDrag);
      cv.addEventListener('pointercancel', endDrag);
    }

    if (touchDrag) {
      let tDrag = false, tX = 0, tY = 0;
      const atT = t => {
        const r = cv.getBoundingClientRect();
        if (!r.width || !r.height) return { x: 0, y: 0 };
        return { x: (t.clientX - r.left) * (W / r.width), y: (t.clientY - r.top) * (H / r.height) };
      };
      cv.addEventListener('touchstart', e => {
        if (!art || !hitBox || e.touches.length !== 1) return;
        const p = atT(e.touches[0]);
        if (p.x < hitBox.x || p.x > hitBox.x + hitBox.w ||
            p.y < hitBox.y || p.y > hitBox.y + hitBox.h) return;
        tDrag = true; tX = p.x; tY = p.y;
        cv.classList.add('is-dragging');
        e.preventDefault();
      }, { passive: false });
      cv.addEventListener('touchmove', e => {
        if (!tDrag || !hitBox) return;
        e.preventDefault();
        const p = atT(e.touches[0]);
        nudgeX += (p.x - tX) / hitBox.sc;
        nudgeY += (p.y - tY) / hitBox.sc;
        tX = p.x; tY = p.y;
        draw();
      }, { passive: false });
      const endT = () => { if (!tDrag) return; tDrag = false; cv.classList.remove('is-dragging'); };
      cv.addEventListener('touchend', endT);
      cv.addEventListener('touchcancel', endT);
    }

    draw();
  }

  /* ---------- hero: animated auto-nesting ---------- */
  function initNest(root) {
    const cv = root.querySelector('[data-dtf-nest]');
    if (!cv || cv.dataset.dtfReady) return;
    cv.dataset.dtfReady = '1';

    const g = cv.getContext('2d'), W = cv.width, H = cv.height;
    const elC = root.querySelector('[data-dtf-count]');
    const elF = root.querySelector('[data-dtf-fill]');
    const elP = root.querySelector('[data-dtf-cost]');
    // A sheet costs the same however full it is, so the number that moves —
    // and the one that sells auto-nesting — is cost per placement.
    const price = parseFloat(cv.dataset.sheetPrice) || 0;
    const cur = cv.dataset.currency || 'R';
    const PAD = 16;
    let placed = [], t0 = 0, raf = 0, seed = 7;

    const build = () => {
      placed = pack(W - PAD * 2, H - PAD * 2, makePieces(64, seed, 1), 9);
      seed = (seed * 31 + 17) % 99991;
    };

    function frame(ts) {
      if (!t0) t0 = ts;
      const el = ts - t0, STEP = 46, FADE = 340;
      sheetBg(g, W, H);
      let shown = 0, area = 0;
      placed.forEach((p, i) => {
        const local = el - i * STEP;
        if (local <= 0) return;
        const k = reduced() ? 1 : Math.min(1, local / FADE);
        const e = 1 - Math.pow(1 - k, 3);
        shown++; area += p.w * p.h;
        g.save();
        g.globalAlpha = e;
        const sc = .72 + .28 * e;
        g.translate(PAD + p.x + p.w / 2, PAD + p.y + p.h / 2);
        g.scale(sc, sc); g.translate(-p.w / 2, -p.h / 2);
        drawMark(g, 0, 0, p.w, p.h, p.kind, p.fill);
        g.restore();
      });
      const fill = area / ((W - PAD * 2) * (H - PAD * 2));
      if (elC) elC.textContent = shown;
      if (elF) elF.textContent = Math.round(fill * 100) + '%';
      if (elP) elP.textContent = shown && price ? cur + (price / shown).toFixed(2) : '—';
      if (shown < placed.length) raf = requestAnimationFrame(frame);
    }

    const run = () => { cancelAnimationFrame(raf); t0 = 0; build(); raf = requestAnimationFrame(frame); };
    const btn = root.querySelector('[data-dtf-renest]');
    if (btn) btn.addEventListener('click', run);
    run();
  }

  /* ---------- product route thumbnails ---------- */
  function initRouteVis(root) {
    root.querySelectorAll('[data-dtf-vis]').forEach(cv => {
      if (cv.dataset.dtfReady) return;
      cv.dataset.dtfReady = '1';
      const g = cv.getContext('2d'), W = cv.width, H = cv.height;
      const kind = cv.dataset.dtfVis;
      const onPaper = kind === 'tee-single' || kind === 'tee-sample' || kind === 'build';
      (onPaper ? sheetLight : sheetBg)(g, W, H);

      if (kind === 'tee-single') {
        drawTee(g, W * .26, H * .10, W * .48, H * .80, '#0A1240', 'rgba(2,11,65,.18)');
        const px = W * .26 + W * .48 * .30, py = H * .10 + H * .80 * .24,
              pw = W * .48 * .40, ph = H * .80 * .36;
        g.fillStyle = 'rgba(253,99,0,.14)'; g.fillRect(px, py, pw, ph);
        g.strokeStyle = FLAME; g.lineWidth = 2; g.setLineDash([7, 5]);
        g.strokeRect(px, py, pw, ph); g.setLineDash([]);
        drawMark(g, px + pw * .14, py + ph * .16, pw * .72, ph * .62, 1, FLAME);
        g.font = '600 15px ui-monospace,monospace'; g.fillStyle = 'rgba(2,11,65,.55)';
        g.fillText('A3 \u00b7 297 \u00d7 420 mm', W * .26, H * .05);

      } else if (kind === 'tee-sample') {
        drawTee(g, W * .10, H * .14, W * .40, H * .72, '#7A85B8', 'rgba(2,11,65,.16)');
        drawTee(g, W * .46, H * .10, W * .44, H * .78, '#0A1240', 'rgba(2,11,65,.2)');
        const px = W * .46 + W * .44 * .28, py = H * .10 + H * .78 * .26,
              pw = W * .44 * .44, ph = H * .78 * .30;
        drawMark(g, px, py, pw, ph, 3, FLAME);
        g.font = '600 15px ui-monospace,monospace'; g.fillStyle = 'rgba(2,11,65,.55)';
        g.fillText('SAMPLE PACK', W * .10, H * .08);

      } else if (kind === 'single') {
        [[.72, .74, 'A3'], [.5, .52, 'A4'], [.26, .28, 'POCKET']].forEach(([sw, sh, lab], i) => {
          const w = W * sw, h = H * sh, x = (W - w) / 2, y = (H - h) / 2;
          g.strokeStyle = i === 2 ? FLAME : 'rgba(255,255,255,.34)';
          g.lineWidth = i === 2 ? 3 : 1.5;
          g.setLineDash(i === 2 ? [] : [7, 6]);
          g.strokeRect(x, y, w, h);
          g.setLineDash([]);
          g.font = '600 17px ui-monospace,monospace';
          g.fillStyle = i === 2 ? FLAME : 'rgba(255,255,255,.5)';
          g.fillText(lab, x + 9, y + 24);
        });
        drawMark(g, W / 2 - 42, H / 2 - 34, 84, 68, 1, FLAME);

      } else if (kind === 'build') {
        const lp = ['#020B41', FLAME, '#8892C4', FLAME, '#C9CEE6'];
        pack(W - 40, H - 40, makePieces(26, 13, .62), 8)
          .forEach((p, i) => drawMark(g, 20 + p.x, 20 + p.y, p.w, p.h, p.kind, lp[i % lp.length]));

      } else {
        g.globalAlpha = .32;
        pack(W - 40, H - 40, makePieces(22, 41, .6), 8)
          .forEach(p => drawMark(g, 20 + p.x, 20 + p.y, p.w, p.h, p.kind, p.fill));
        g.globalAlpha = 1;
        g.fillStyle = 'rgba(2,11,65,.78)'; g.fillRect(0, 0, W, H);
        g.textAlign = 'center';
        g.font = '700 92px ui-monospace,monospace';
        g.fillStyle = FLAME; g.fillText('× 25', W / 2, H / 2 + 32);
        g.font = '600 17px ui-monospace,monospace';
        g.fillStyle = 'rgba(255,255,255,.62)';
        g.fillText('QUANTITY', W / 2, H / 2 - 44);
      }
    });
  }

  /* ---------- halftone instrument ---------- */
  function initHalftone(root) {
    const stage = root.querySelector('[data-dtf-ht]');
    if (!stage || stage.dataset.dtfReady) return;
    stage.dataset.dtfReady = '1';

    const base = stage.querySelector('[data-dtf-ht-base]');
    const over = stage.querySelector('[data-dtf-ht-over]');
    const handle = stage.querySelector('[data-dtf-ht-handle]');
    if (!base || !over) return;

    const W = base.width, H = base.height;
    const src = document.createElement('canvas'); src.width = W; src.height = H;
    const sg = src.getContext('2d', { willReadFrequently: true });

    // Gradient-filled emblem, so dot size genuinely varies across the artwork
    // the way it would on a real halftoned design.
    const cx = W / 2, cy = H * .46, rw = W * .34, rh = H * .40;
    const grd = sg.createRadialGradient(cx - rw * .35, cy - rh * .4, 10, cx, cy, rw * 1.5);
    grd.addColorStop(0, '#ffffff'); grd.addColorStop(.42, '#c4c4c4'); grd.addColorStop(1, '#242424');
    sg.fillStyle = grd;
    sg.beginPath();
    sg.moveTo(cx, cy - rh);
    sg.bezierCurveTo(cx + rw, cy - rh, cx + rw, cy + rh * .3, cx, cy + rh);
    sg.bezierCurveTo(cx - rw, cy + rh * .3, cx - rw, cy - rh, cx, cy - rh);
    sg.closePath(); sg.fill();
    sg.globalCompositeOperation = 'destination-out';
    sg.lineWidth = W * .022;
    sg.beginPath(); sg.arc(cx, cy - rh * .1, rw * .58, 0, 7); sg.stroke();
    sg.globalCompositeOperation = 'source-over';
    sg.fillStyle = '#f2f2f2';
    sg.fillRect(cx - rw * .9, cy + rh * .78, rw * 1.8, H * .055);
    sg.fillStyle = '#7d7d7d';
    sg.fillRect(cx - rw * .6, cy + rh * .78 + H * .078, rw * 1.2, H * .034);

    const px = sg.getImageData(0, 0, W, H).data;

    // Tint on its own canvas first — tinting after the background fill would
    // make source-atop cover every pixel, not just the artwork.
    const tint = document.createElement('canvas'); tint.width = W; tint.height = H;
    const tg = tint.getContext('2d');
    tg.drawImage(src, 0, 0);
    tg.globalCompositeOperation = 'source-atop';
    tg.fillStyle = FLAME; tg.fillRect(0, 0, W, H);

    const bg = base.getContext('2d');
    bg.fillStyle = '#051133'; bg.fillRect(0, 0, W, H);
    bg.drawImage(tint, 0, 0);

    const inkAt = (x, y) => {
      x |= 0; y |= 0;
      if (x < 0 || y < 0 || x >= W || y >= H) return 0;
      const i = (y * W + x) * 4, a = px[i + 3] / 255;
      if (!a) return 0;
      return a * (px[i] * .2126 + px[i + 1] * .7152 + px[i + 2] * .0722) / 255;
    };

    let solidInk = 0;
    for (let y = 0; y < H; y += 2) for (let x = 0; x < W; x += 2) solidInk += inkAt(x, y) * 4;

    const cF = root.querySelector('[data-dtf-freq]');
    const cA = root.querySelector('[data-dtf-angle]');
    const cG = root.querySelector('[data-dtf-gain]');
    const vF = root.querySelector('[data-dtf-vfreq]');
    const vA = root.querySelector('[data-dtf-vangle]');
    const vG = root.querySelector('[data-dtf-vgain]');
    const vS = root.querySelector('[data-dtf-vsave]');

    function render() {
      const lpi = cF ? +cF.value : 45;
      const deg = cA ? +cA.value : 45;
      const gain = (cG ? +cG.value : 85) / 100;
      const g = over.getContext('2d');
      g.fillStyle = '#051133'; g.fillRect(0, 0, W, H);
      g.fillStyle = FLAME;

      const spacing = W / lpi, rad = deg * Math.PI / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad), diag = Math.hypot(W, H);
      let dotInk = 0;

      for (let v = -diag; v < diag; v += spacing) {
        for (let u = -diag; u < diag; u += spacing) {
          const x = u * cos - v * sin + W / 2;
          const y = u * sin + v * cos + H / 2;
          if (x < -spacing || y < -spacing || x > W + spacing || y > H + spacing) continue;
          const ink = inkAt(x, y);
          if (ink < .04) continue;
          const r = Math.sqrt(ink) * (spacing / 2) * gain;
          if (r < .35) continue;
          g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
          dotInk += Math.PI * r * r;
        }
      }
      if (vF) vF.textContent = lpi;
      if (vA) vA.textContent = deg;
      if (vG) vG.textContent = gain.toFixed(2);
      if (vS) vS.textContent = Math.max(0, Math.min(99, (1 - dotInk / solidInk) * 100)).toFixed(0) + '%';
    }
    [cF, cA, cG].forEach(c => c && c.addEventListener('input', render));
    render();

    let dragging = false;
    const setSplit = clientX => {
      const r = stage.getBoundingClientRect();
      const pct = Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100));
      stage.style.setProperty('--split', pct + '%');
      if (handle) handle.style.left = pct + '%';
    };
    stage.style.setProperty('--split', '55%');
    stage.addEventListener('pointerdown', e => {
      dragging = true; stage.setPointerCapture(e.pointerId); setSplit(e.clientX);
    });
    stage.addEventListener('pointermove', e => { if (dragging) setSplit(e.clientX); });
    stage.addEventListener('pointerup', () => { dragging = false; });
    stage.addEventListener('pointercancel', () => { dragging = false; });
  }


  /* ---------- VAT toggle on the price ladder ---------- */
  function initVat(root) {
    root.querySelectorAll('[data-dtf-vat-group]').forEach(group => {
      if (group.dataset.dtfReady) return;
      group.dataset.dtfReady = '1';
      // The scope is a SIBLING of the toggle, not an ancestor — resolve it from
      // the section wrapper. closest() would fall through to `document`, which
      // has no classList and throws inside the click handler.
      const section = group.closest('.dtf');
      const scope = section && section.querySelector('[data-dtf-vat-scope]');
      if (!scope) return;
      group.querySelectorAll('button[data-dtf-vat]').forEach(btn => {
        btn.addEventListener('click', () => {
          const ex = btn.dataset.dtfVat === 'ex';
          scope.classList.toggle('dtf-vat-ex', ex);
          group.querySelectorAll('button[data-dtf-vat]').forEach(b => {
            const on = b === btn;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on);
          });
        });
      });
    });
  }

  /* ---------- cost-per-transfer calculator ----------
     Works out how many pieces fit across a fixed-width sheet, how long the
     sheet has to be, which published band that lands in, and what each
     transfer therefore costs. Mirrors how the gang sheet is actually priced. */
  function initCalc(root) {
    const el = root.querySelector('[data-dtf-calc]');
    if (!el || el.dataset.dtfReady) return;
    el.dataset.dtfReady = '1';

    let bands = [];
    const src = el.querySelector('[data-dtf-bands]');
    try { bands = JSON.parse(src.textContent) || []; } catch (e) { bands = []; }
    bands = bands.filter(b => b.len > 0).sort((a, b) => a.len - b.len);

    const sheetW   = parseFloat(el.dataset.sheetWidth) || 600;
    const gap      = parseFloat(el.dataset.gap) || 5;
    const tradeLen = parseFloat(el.dataset.tradeLen) || 0;   // mm at which trade rate starts
    const tradeIn  = parseFloat(el.dataset.tradeIncl) || 0;  // per linear metre, incl VAT
    const tradeEx  = parseFloat(el.dataset.tradeEx) || 0;
    const cur      = el.dataset.currency || 'R';

    const inW   = el.querySelector('[data-dtf-w]');
    const inH   = el.querySelector('[data-dtf-h]');
    const inQ   = el.querySelector('[data-dtf-qty]');
    const inRot = el.querySelector('[data-dtf-rotate]');
    const cv    = el.querySelector('[data-dtf-calc-canvas]');
    const out   = {
      sheet: el.querySelector('[data-dtf-o-sheet]'),
      fit:   el.querySelector('[data-dtf-o-fit]'),
      price: el.querySelector('[data-dtf-o-price]'),
      each:  el.querySelector('[data-dtf-o-each]'),
      note:  el.querySelector('[data-dtf-o-note]')
    };

    // Match the client's own rate card: comma thousands, full-stop decimal.
    // en-ZA formats 264.50 as "264,50", which would clash with the table above.
    const money = n => {
      const [whole, frac] = Math.abs(n).toFixed(2).split('.');
      return cur + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + frac;
    };

    // best orientation = the one needing the shortest sheet
    function layout(w, h, qty, rotate) {
      const tries = rotate ? [[w, h], [h, w]] : [[w, h]];
      let best = null;
      for (const [pw, ph] of tries) {
        if (pw > sheetW) continue;
        const perRow = Math.floor((sheetW + gap) / (pw + gap));
        if (perRow < 1) continue;
        const rows = Math.ceil(qty / perRow);
        const len = rows * (ph + gap) - gap;
        if (!best || len < best.len) best = { pw, ph, perRow, rows, len };
      }
      return best;
    }

    function draw(lay, sheetLen) {
      if (!cv) return;
      const g = cv.getContext('2d'), W = cv.width, H = cv.height;
      g.fillStyle = '#051133'; g.fillRect(0, 0, W, H);
      if (!lay || !sheetLen) return;

      // sheet drawn on its side: length across, width down
      const pad = 16;
      const scale = Math.min((W - pad * 2) / sheetLen, (H - pad * 2) / sheetW);
      const sw = sheetLen * scale, sh = sheetW * scale;
      const ox = (W - sw) / 2, oy = (H - sh) / 2;

      g.fillStyle = '#040D2E'; g.fillRect(ox, oy, sw, sh);
      g.strokeStyle = 'rgba(253,99,0,.5)'; g.lineWidth = 2;
      g.strokeRect(ox, oy, sw, sh);

      const qty = Math.max(0, parseInt(inQ.value, 10) || 0);
      let n = 0;
      for (let r = 0; r < lay.rows && n < qty; r++) {
        for (let c = 0; c < lay.perRow && n < qty; c++, n++) {
          // row index runs along the sheet length (x), column across width (y)
          const x = ox + (r * (lay.ph + gap)) * scale;
          const y = oy + (c * (lay.pw + gap)) * scale;
          g.fillStyle = n % 4 === 0 ? FLAME : (n % 4 === 1 ? WHITE : MID);
          g.globalAlpha = .92;
          g.fillRect(x, y, lay.ph * scale, lay.pw * scale);
        }
      }
      g.globalAlpha = 1;
    }

    function update() {
      const w = parseFloat(inW.value) || 0;
      const h = parseFloat(inH.value) || 0;
      const qty = Math.max(1, parseInt(inQ.value, 10) || 0);
      const rotate = inRot ? inRot.checked : true;

      const blank = () => { out.sheet.textContent = out.fit.textContent = '—'; out.price.innerHTML = '—'; out.each.textContent = '—'; };

      if (w <= 0 || h <= 0) { blank(); out.note.textContent = 'Enter your artwork size to price a sheet.'; out.note.classList.remove('is-warn'); draw(null); return; }

      const lay = layout(w, h, qty, rotate);
      if (!lay) {
        blank();
        out.note.textContent = `Artwork is wider than the ${sheetW} mm sheet, even rotated. Split it across two sheets or reduce the size.`;
        out.note.classList.add('is-warn');
        draw(null);
        return;
      }

      const band = bands.find(b => b.len >= lay.len);
      let priceIn, priceEx, sheetLabel, sheetLen, note = '', warn = false;

      if (band) {
        priceIn = band.incl; priceEx = band.ex; sheetLabel = band.label; sheetLen = band.len;
      } else if (tradeIn) {
        // past the published ladder — charged per linear metre
        const metres = lay.len / 1000;
        priceIn = metres * tradeIn; priceEx = metres * tradeEx;
        sheetLen = lay.len;
        sheetLabel = (Math.ceil(metres * 100) / 100).toFixed(2) + ' m';
        const longest = bands.length ? bands[bands.length - 1].len : 0;
        if (lay.len > longest && lay.len < tradeLen) {
          note = `This needs ${sheetLabel}, which falls between the longest published sheet and the ${(tradeLen / 1000)} m trade rate. Priced here at the trade rate — confirm before quoting.`;
          warn = true;
        } else {
          note = `Priced at the ${(tradeLen / 1000)} m+ trade rate of ${money(tradeIn)} per linear metre.`;
        }
      } else { blank(); return; }

      const each = priceIn / qty;
      const used = qty * lay.pw * lay.ph;
      const fill = Math.round((used / (sheetW * sheetLen)) * 100);

      out.sheet.innerHTML = sheetLabel + `<small>${sheetW} &times; ${Math.round(sheetLen)} mm</small>`;
      out.fit.innerHTML = `${lay.perRow} across`  + `<small>${lay.rows} row${lay.rows === 1 ? '' : 's'} &middot; ${fill}% of sheet used</small>`;
      out.price.innerHTML = money(priceIn) + `<small>${money(priceEx)} excl. VAT</small>`;
      out.each.innerHTML = money(each) + `<small>per transfer, incl. VAT</small>`;

      if (!note) {
        note = lay.len < sheetLen
          ? `Your artwork needs ${Math.round(lay.len)} mm, so it is priced on the next sheet up at ${sheetLabel}. Adding more pieces costs nothing until you fill it.`
          : `Fits exactly on the ${sheetLabel} sheet.`;
      }
      out.note.textContent = note;
      out.note.classList.toggle('is-warn', warn);
      draw(lay, sheetLen);
    }

    [inW, inH, inQ, inRot].forEach(i => i && i.addEventListener('input', update));
    el.querySelectorAll('[data-dtf-preset]').forEach(btn => {
      btn.addEventListener('click', () => {
        const [w, h, q] = btn.dataset.dtfPreset.split(',');
        inW.value = w; inH.value = h; if (q) inQ.value = q;
        update();
      });
    });
    update();
  }


  /* ---------- header menu panel ----------
     One navigation model at every width: the bar carries the promoted links,
     the panel carries the whole index. While it is open it behaves as a
     dialog - page scroll locked, the regions behind it inert, focus kept
     between the menu button and the panel, Escape closing back to the button.
     Symmetry's own header JS is not on the page (dtf-nav replaces that
     section), so none of this can be inherited from it. */
  function initNavPanel(root) {
    root.querySelectorAll('[data-dtf-nav-toggle]').forEach(btn => {
      if (btn.dataset.dtfReady) return;
      btn.dataset.dtfReady = '1';

      const nav = btn.closest('[data-dtf-nav]');
      if (!nav) return;
      const bar   = nav.querySelector('.dtf-nav__in');
      const panel = nav.querySelector('[data-dtf-nav-panel]');
      const scrim = nav.querySelector('[data-dtf-nav-scrim]');
      if (!bar || !panel) return;

      const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
                        'select:not([disabled]),textarea:not([disabled]),' +
                        '[tabindex]:not([tabindex="-1"])';
      let hideTimer = null;

      const isOpen = () => nav.classList.contains('is-open');

      /* The page behind, for `inert`. aria-modal already hides the rest from
         assistive tech; this stops the main content taking Tab as well. */
      function behind() {
        const own = nav.closest('[id^="shopify-section-"]') || nav;
        const list = [document.getElementById('content'), document.getElementById('pagefooter')];
        let sib = own.previousElementSibling;
        while (sib) {
          if (sib.id && sib.id.indexOf('shopify-section-') === 0) list.push(sib);
          sib = sib.previousElementSibling;
        }
        return list.filter(Boolean);
      }

      function lock(on) {
        const html = document.documentElement;
        if (on) {
          // Hiding the scrollbar shifts the layout unless the width is given back.
          const gutter = window.innerWidth - html.clientWidth;
          if (gutter > 0) html.style.paddingRight = gutter + 'px';
          html.classList.add('dtf-nav-locked');
        } else {
          html.classList.remove('dtf-nav-locked');
          html.style.paddingRight = '';
        }
      }

      /* The panel hangs off the bottom of the sticky bar, so its height budget
         is whatever the bar leaves. Scroll is locked while open, so this only
         needs recomputing on resize. */
      function measure() {
        nav.style.setProperty('--dtf-nav-b',
          Math.ceil(bar.getBoundingClientRect().bottom) + 'px');
      }

      function open() {
        if (isOpen()) return;
        clearTimeout(hideTimer);
        measure();
        panel.hidden = false;
        if (scrim) scrim.hidden = false;
        lock(true);
        behind().forEach(el => { el.inert = true; });
        btn.setAttribute('aria-expanded', 'true');
        // hidden -> visible in the same frame would skip the transition
        requestAnimationFrame(() => nav.classList.add('is-open'));
        const first = panel.querySelector(FOCUSABLE);
        if (first) first.focus({ preventScroll: true });
      }

      function close(returnFocus) {
        if (!isOpen()) return;
        nav.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        behind().forEach(el => { el.inert = false; });
        lock(false);
        if (returnFocus) btn.focus();
        hideTimer = setTimeout(() => {
          panel.hidden = true;
          if (scrim) scrim.hidden = true;
        }, 240);
      }

      btn.addEventListener('click', () => (isOpen() ? close(true) : open()));
      if (scrim) scrim.addEventListener('click', () => close(true));
      panel.querySelectorAll('a[href]').forEach(a =>
        a.addEventListener('click', () => close(false)));

      document.addEventListener('keydown', e => {
        if (!isOpen()) return;
        if (e.key === 'Escape') { close(true); return; }
        if (e.key !== 'Tab') return;

        // The menu button is the panel's close control, so it stays in the cycle.
        const nodes = [btn].concat(Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)));
        if (!nodes.length) return;
        const first = nodes[0];
        const last  = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !nav.contains(active))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault(); first.focus();
        }
      });

      window.addEventListener('resize', () => { if (isOpen()) measure(); });

      // so the cart link, the editor and the teardown can drive it
      nav._dtfNavClose = close;
      nav._dtfNavOpen = open;
    });
  }

  /* ---------- nav hide on scroll ----------
     Only when the section's scroll setting asks for it. Down hides the bar,
     up brings it back. It waits for a little upward travel
     rather than any at all, so the jitter of a trackpad or a thumb settling
     doesn't flash it in and out. Page-level, so it binds once however many
     times the editor re-inits. */
  const NAV_SHOW_AFTER = 40; // px of upward scroll before the bar returns
  function initNavHide() {
    if (window._dtfNavHide) return;
    window._dtfNavHide = true;

    const html = document.documentElement;
    let lastY = Math.max(window.scrollY, 0);
    let upTravel = 0;
    let ticking = false;

    const show = () => html.classList.remove('dtf-nav-away');

    function update() {
      ticking = false;
      const wrap = document.querySelector('.section-dtf-nav');
      const y = Math.max(window.scrollY, 0); // iOS overscroll goes negative
      const dy = y - lastY;
      lastY = y;
      // An open menu or drawer locks scroll; nothing should move under it.
      if (!wrap || html.classList.contains('dtf-nav-locked')) return;
      // Read every time rather than once: the editor can change the setting
      // mid-visit, and switching away from "hide" must not strand it hidden.
      const nav = wrap.querySelector('[data-dtf-nav]');
      if (!nav || nav.dataset.dtfNavScroll !== 'hide') { upTravel = 0; show(); return; }

      // Until the page has scrolled past the whole header there is nothing
      // to hide it from, and hiding it would open a gap above the content.
      const content = document.getElementById('content');
      const headerEnd = content ? content.getBoundingClientRect().top + y : wrap.offsetHeight;
      if (y <= headerEnd) { upTravel = 0; show(); return; }

      if (dy > 0) {
        upTravel = 0;
        // Keyboard focus inside the bar keeps it on screen. Only keyboard focus:
        // a clicked menu button or cart link keeps focus too, and would
        // otherwise pin the bar for the rest of the visit.
        const f = document.activeElement;
        const kbFocus = f && wrap.contains(f) && f.matches(':focus-visible');
        if (!kbFocus) html.classList.add('dtf-nav-away');
      } else if (dy < 0) {
        upTravel -= dy;
        if (upTravel >= NAV_SHOW_AFTER) show();
      }
    }

    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    // tabbing into a hidden bar has to bring it back
    document.addEventListener('focusin', e => {
      const t = e.target;
      if (t.closest && t.closest('.section-dtf-nav') && t.matches(':focus-visible')) show();
    });
    // the editor can't highlight what's off screen, and a re-render after the
    // scroll setting changes shouldn't leave the bar hidden until next scroll
    ['shopify:section:select', 'shopify:section:load'].forEach(type =>
      document.addEventListener(type, e => {
        if (e.target.classList.contains('section-dtf-nav')) show();
      }));
  }

  /* ---------- header cart ----------
     The link is a real link to /cart and stays that way when no drawer is on
     the page (the cart template, or the cart type set to "page"). Where the
     drawer exists it is opened through cart-drawer.js's own documented event
     rather than by reaching into the element. */
  function initNavCart(root) {
    root.querySelectorAll('[data-dtf-cart]').forEach(link => {
      if (link.dataset.dtfReady) return;
      link.dataset.dtfReady = '1';

      link.addEventListener('click', e => {
        const drawer = document.querySelector('.js-cart-drawer');
        const type = window.theme && theme.settings && theme.settings.cartType;
        if (!drawer || type !== 'drawer') return;
        e.preventDefault();
        // The drawer must not open while the page behind the panel is inert.
        const nav = link.closest('[data-dtf-nav]');
        if (nav && nav._dtfNavClose) nav._dtfNavClose(false);
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open',
          { detail: { opener: link } }));
      });
    });

    // Symmetry's store-header is what normally refreshes the count, and it is
    // not on the page, so the badge is kept in step here instead.
    if (!document.documentElement.dataset.dtfCartSync) {
      document.documentElement.dataset.dtfCartSync = '1';
      document.addEventListener('on:cart:change', syncCart);
    }
  }

  function syncCart() {
    const base = (window.theme && theme.routes && theme.routes.cart) || '/cart';
    fetch(base + '.js', { headers: { 'Content-Type': 'application/json' } })
      .then(r => (r.ok ? r.json() : null))
      .then(cart => {
        if (!cart) return;
        document.querySelectorAll('[data-dtf-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        return cartHash(cart.items);
      })
      .then(hash => {
        // The theme compares this on pageshow; leaving it stale would have it
        // announce a cart change on every return to the page.
        if (hash) document.querySelectorAll('[data-dtf-cart]')
          .forEach(el => { el.dataset.hash = hash; });
      })
      .catch(() => {});
  }

  /* Same string and digest the theme's own pageshow check builds. */
  function cartHash(items) {
    if (!items || !window.crypto || !crypto.subtle) return null;
    const str = items.map(i => i.key + '|' + i.quantity).join(',');
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
      .then(buf => Array.from(new Uint8Array(buf))
        .map(x => x.toString(16).padStart(2, '0')).join(''));
  }


  /* ---------- marquee: duplicate the track so the loop is seamless ---------- */
  function initMarquee(root) {
    root.querySelectorAll('[data-dtf-mq]').forEach(mq => {
      if (mq.dataset.dtfReady) return;
      mq.dataset.dtfReady = '1';
      mq.innerHTML = mq.innerHTML + mq.innerHTML;
    });
  }



  /* ---------- resolution checker ----------
     Effective dpi = pixels / inches. Objective arithmetic, no house rules
     beyond the target dpi, which the section exposes as a setting. */
  function initRes(root) {
    const el = root.querySelector('[data-dtf-res]');
    if (!el || el.dataset.dtfReady) return;
    el.dataset.dtfReady = '1';

    const target = parseFloat(el.dataset.target) || 300;
    const floor  = parseFloat(el.dataset.floor) || 150;
    const inPW   = el.querySelector('[data-dtf-px-w]');
    const inPH   = el.querySelector('[data-dtf-px-h]');
    const inMM   = el.querySelector('[data-dtf-print-w]');
    const gauge  = el.querySelector('[data-dtf-gauge]');
    const oDpi   = el.querySelector('[data-dtf-o-dpi]');
    const oVerd  = el.querySelector('[data-dtf-o-verdict]');
    const oFill  = el.querySelector('[data-dtf-o-fill]');
    const oNote  = el.querySelector('[data-dtf-o-note]');
    const oH     = el.querySelector('[data-dtf-o-height]');
    const oMax   = el.querySelector('[data-dtf-o-max]');
    const oPx    = el.querySelector('[data-dtf-o-needed]');

    const txt = {
      good: el.dataset.msgGood || 'Good to print',
      ok:   el.dataset.msgOk   || 'Usable, not ideal',
      bad:  el.dataset.msgBad  || 'Too low to print well'
    };

    function update() {
      const pw = parseFloat(inPW.value) || 0;
      const ph = parseFloat(inPH.value) || 0;
      const mm = parseFloat(inMM.value) || 0;

      if (pw <= 0 || ph <= 0 || mm <= 0) {
        oDpi.textContent = '—';
        oVerd.textContent = '';
        oFill.style.width = '0%';
        oH.textContent = oMax.textContent = oPx.textContent = '—';
        oNote.textContent = 'Enter your file size in pixels and how wide you want it printed.';
        gauge.classList.remove('is-fail');
        return;
      }

      const inches = mm / 25.4;
      const dpi = pw / inches;
      const printH = mm * (ph / pw);
      const maxW = (pw / target) * 25.4;          // widest print at target dpi
      const neededPx = Math.ceil(inches * target); // pixels needed at this size

      oDpi.innerHTML = Math.round(dpi) + '<small>dpi</small>';
      oH.textContent = Math.round(printH) + ' mm';
      oMax.textContent = Math.round(maxW) + ' mm';
      oPx.textContent = neededPx.toLocaleString('en-GB') + ' px';

      // scale the bar so the target sits at 75% of the track
      oFill.style.width = Math.max(2, Math.min(100, (dpi / target) * 75)) + '%';

      // Compare on the ROUNDED dpi so the verdict can never contradict the
      // number displayed — 299.97 shows as "300 dpi" and must read as good.
      const shown = Math.round(dpi);
      const fail = shown < floor;
      gauge.classList.toggle('is-fail', fail);

      if (shown >= target) {
        oVerd.textContent = txt.good;
        oNote.innerHTML = `At ${Math.round(mm)} mm wide this file gives you <strong>${Math.round(dpi)} dpi</strong>, at or above the ${target} dpi we print to. You could go up to ${Math.round(maxW)} mm and still be fine.`;
      } else if (!fail) {
        oVerd.textContent = txt.ok;
        oNote.innerHTML = `This lands at <strong>${Math.round(dpi)} dpi</strong>. It will print, but fine detail and edges will soften. For ${target} dpi at this size you would need a ${neededPx.toLocaleString('en-GB')} px wide file, or print it at ${Math.round(maxW)} mm instead.`;
      } else {
        oVerd.textContent = txt.bad;
        oNote.innerHTML = `<strong>${Math.round(dpi)} dpi</strong> is below the ${floor} dpi floor. Enlarging a small file does not add detail — you need the artwork re-exported at ${neededPx.toLocaleString('en-GB')} px wide, or printed no wider than ${Math.round(maxW)} mm.`;
      }
    }

    [inPW, inPH, inMM].forEach(i => i && i.addEventListener('input', update));
    el.querySelectorAll('[data-dtf-res-preset]').forEach(b => {
      b.addEventListener('click', () => {
        const [w, h, mm] = b.dataset.dtfResPreset.split(',');
        inPW.value = w; inPH.value = h; inMM.value = mm;
        update();
      });
    });
    update();
  }

  /* ---------- FAQ hub: category chips + text filter ---------- */
  function initFaqHub(root) {
    root.querySelectorAll('[data-dtf-hub]').forEach(hub => {
      if (hub.dataset.dtfReady) return;
      hub.dataset.dtfReady = '1';

      const search  = hub.querySelector('[data-dtf-hub-search]');
      const clear   = hub.querySelector('[data-dtf-hub-clear]');
      const chips   = [...hub.querySelectorAll('[data-dtf-cat]')];
      const items   = [...hub.querySelectorAll('details[data-cat]')];
      const groups  = [...hub.querySelectorAll('[data-group]')];
      const count   = hub.querySelector('[data-dtf-hub-count]');
      const empty   = hub.querySelector('[data-dtf-hub-empty]');
      const single  = hub.dataset.dtfHubSingular || 'answer';
      const plural  = hub.dataset.dtfHubPlural || 'answers';
      let cat = 'all';

      const norm = s => (s || '').toLowerCase();

      function apply() {
        const q = norm(search && search.value).trim();
        let shown = 0;
        const liveCats = new Set();

        items.forEach(d => {
          const matchesCat  = cat === 'all' || d.dataset.cat === cat;
          const matchesText = !q || norm(d.textContent).includes(q);
          const on = matchesCat && matchesText;
          d.hidden = !on;
          if (on) { shown++; liveCats.add(d.dataset.cat); }
          // collapse anything filtered out so reopening starts clean
          if (!on) d.open = false;
        });

        groups.forEach(g => { g.hidden = !liveCats.has(g.dataset.group); });
        if (count) count.textContent = `${shown} ${shown === 1 ? single : plural}`;
        if (empty) empty.hidden = shown > 0;
        if (clear) clear.hidden = !q;
      }

      chips.forEach(c => c.addEventListener('click', () => {
        cat = c.dataset.dtfCat;
        chips.forEach(o => {
          const on = o === c;
          o.classList.toggle('is-on', on);
          o.setAttribute('aria-pressed', on);
        });
        apply();
      }));

      if (search) search.addEventListener('input', apply);
      if (clear) clear.addEventListener('click', () => {
        search.value = ''; search.focus(); apply();
      });
      apply();
    });
  }

  /* ---------- trust strip: one claim per view, advancing itself on mobile ---
     Only ever runs while the track actually overflows, which on this section
     is true at phone widths and false once the chips fit in a wrapped row.
     That check lives inside the tick rather than behind a resize listener, so
     rotating a phone or dragging a desktop window narrow sorts itself out. */
  function initTrustScroll(root) {
    root.querySelectorAll('[data-dtf-trust-scroll]').forEach(track => {
      if (track.dataset.dtfReady) return;
      track.dataset.dtfReady = '1';

      const delay = (parseFloat(track.dataset.dtfTrustScroll) || 4) * 1000;
      if (!(delay > 0)) return;

      let timer = null, held = false, seen = true;
      const stop = () => { clearInterval(timer); timer = null; };
      const overflows = () => track.scrollWidth - track.clientWidth > 4;

      function advance() {
        // the theme editor swaps a section's markup wholesale; if that happened
        // we're pointing at a detached node and should retire quietly
        if (!track.isConnected) return stop();
        if (held || seen === false || document.hidden || reduced() || !overflows()) return;
        const items = Array.from(track.children);
        if (items.length < 2) return;
        // first box starting past the current offset, or back to the top
        const next = items.find(el => el.offsetLeft > track.scrollLeft + 4) || items[0];
        track.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
      }

      /* Movement stops the moment anyone touches, hovers or tabs into the row,
         and never starts for prefers-reduced-motion. Keeps WCAG 2.2.2 in reach
         without bolting a pause button onto a slim strip. */
      ['pointerdown', 'pointerenter', 'touchstart', 'focusin'].forEach(t =>
        track.addEventListener(t, () => { held = true; }, { passive: true }));
      ['pointerleave', 'focusout'].forEach(t =>
        track.addEventListener(t, () => { held = false; }, { passive: true }));

      // don't animate a strip that's scrolled out of sight
      if ('IntersectionObserver' in window) {
        seen = false;
        new IntersectionObserver(es => es.forEach(e => { seen = e.isIntersecting; }),
          { threshold: 0.2 }).observe(track);
      }

      track._dtfStop = stop;
      timer = setInterval(advance, delay);
    });
  }

  /* ---------- scroll reveals ----------
     Guarded with its own flag, not the shared data-dtf-ready. The instrument
     sections put .dtf-reveal on the very element their own init claims
     (.dtf-calc, .dtf-res, .dtf-hub, the fitcheck root), and those inits run
     first. Sharing one flag meant they marked themselves ready, this pass
     skipped them as already handled, .is-in never landed, and the four
     interactive sections stayed at opacity:0 - present, sized, invisible. */
  let io;
  function initReveal(root) {
    const items = root.querySelectorAll('.dtf-reveal:not([data-dtf-revealed])');
    if (!items.length) return;
    // In the editor a section is usually rendered off-screen, so waiting for it
    // to intersect would leave it hidden for as long as the merchant is looking
    // at it. Everything else in the theme trusts Shopify.designMode over the
    // body class, so trust it here too and just show them.
    const editing = !!(window.Shopify && window.Shopify.designMode);
    if (editing || reduced() || !('IntersectionObserver' in window)) {
      items.forEach(el => { el.dataset.dtfRevealed = '1'; el.classList.add('is-in'); });
      return;
    }
    io = io || new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -12% 0px' });
    items.forEach(el => { el.dataset.dtfRevealed = '1'; io.observe(el); });
  }

  /* ---------- boot ---------- */
  function init(root) {
    root = root || document;
    initNavPanel(root);
    initNavHide();
    initNavCart(root);
    initMarquee(root);
    initNest(root);
    initHeroBg(root);
    initRouteVis(root);
    initFit(root);
    initSplitArt(root);
    initHalftone(root);
    initVat(root);
    initCalc(root);
    initFaqHub(root);
    initRes(root);
    initTrustScroll(root);
    initReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  // theme editor re-renders a section's markup, so re-bind against it
  document.addEventListener('shopify:section:load', e => init(e.target));

  /* A merchant selecting a nav link block in the editor gets nothing to look
     at while the panel is closed, so open it for them. */
  document.addEventListener('shopify:block:select', e => {
    const nav = e.target.closest('[data-dtf-nav]');
    if (!nav || !nav._dtfNavOpen) return;
    if (e.target.closest('[data-dtf-nav-panel]')) nav._dtfNavOpen();
  });
  document.addEventListener('shopify:block:deselect', e => {
    const nav = e.target.closest('[data-dtf-nav]');
    if (nav && nav._dtfNavClose) nav._dtfNavClose(false);
  });
  // ...and tear down anything holding a timer, or it keeps ticking against
  // markup the editor has already thrown away
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-dtf-trust-scroll]')
      .forEach(el => { if (el._dtfStop) el._dtfStop(); });
    // an open menu panel would otherwise leave the page locked and inert
    e.target.querySelectorAll('[data-dtf-nav]')
      .forEach(el => { if (el._dtfNavClose) el._dtfNavClose(false); });
  });
})();
